import { supabase } from '../lib/supabase';

// Vendor Operations
export const vendorService = {
  // Get all vendors for the organization
  async getVendors(organizationId, filters = {}) {
    try {
      let query = supabase?.from('vendors')?.select(`
          *,
          created_by_profile:user_profiles!vendors_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters - CRITICAL FIX: Never try to filter by bills_count directly
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.search) {
        query = query?.or(`vendor_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%,company_name.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      
      if (error) {
        // ENHANCED: Handle schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error detected:', error?.message);
          throw new Error('Database schema error: bills_count is not a database column. This is calculated at runtime.');
        }
        throw error;
      }
      
      // ENHANCED FIX: Fetch bill counts separately with better error handling and validation
      const vendorsWithCounts = await Promise.allSettled(
        (data || [])?.map(async (vendor) => {
          try {
            // Ensure vendor has valid ID before querying bills
            if (!vendor?.id) {
              console.warn('Vendor missing ID, skipping bill count calculation:', vendor);
              return {
                ...vendor,
                bills_count: 0
              };
            }

            // CRITICAL: Use count query only - never select bills_count column
            const { count: billCount, error: countError } = await supabase
              ?.from('bills')
              ?.select('*', { count: 'exact', head: true })
              ?.eq('vendor_id', vendor?.id)
              ?.eq('organization_id', organizationId);
              
            if (countError) {
              // Handle schema errors gracefully
              if (countError?.code === 'PGRST204' && countError?.message?.includes('bills_count')) {
                console.warn('Ignoring bills_count schema error - using fallback count');
                return {
                  ...vendor,
                  bills_count: 0
                };
              }
              
              console.warn(`Error fetching bill count for vendor ${vendor?.id}:`, countError);
              return {
                ...vendor,
                bills_count: 0 // Always ensure bills_count exists as a number
              };
            }
              
            // CRITICAL: Ensure bills_count is always a valid number
            const validBillCount = typeof billCount === 'number' && !isNaN(billCount) ? billCount : 0;
            
            return {
              ...vendor,
              bills_count: validBillCount
            };
          } catch (countError) {
            // ENHANCED: Handle schema cache errors in bill counting
            if (countError?.code === 'PGRST204' && countError?.message?.includes('bills_count')) {
              console.warn('Schema cache error in bill counting - using fallback');
              return {
                ...vendor,
                bills_count: 0
              };
            }
            
            console.warn(`Exception while fetching bill count for vendor ${vendor?.id}:`, countError);
            return {
              ...vendor,
              bills_count: 0 // Always ensure bills_count exists as a number
            };
          }
        })
      );
      
      // Extract successful results and handle failed ones with enhanced validation
      const processedVendors = vendorsWithCounts?.map((result, index) => {
        if (result?.status === 'fulfilled') {
          const vendor = result?.value;
          // Double-check that bills_count is properly set and is a valid number
          if (typeof vendor?.bills_count !== 'number' || isNaN(vendor?.bills_count)) {
            vendor.bills_count = 0;
          }
          return vendor;
        } else {
          console.warn('Failed to process vendor with bill count:', result?.reason);
          // Return vendor with guaranteed bills_count property
          const vendorData = data?.[index] || {};
          return {
            ...vendorData,
            bills_count: 0 // Always ensure bills_count exists as a number
          };
        }
      })?.filter(vendor => vendor?.id); // Filter out any invalid vendors
      
      return { data: processedVendors || [], error: null };
    } catch (error) {
      // ENHANCED: Handle PGRST204 schema cache errors specifically
      if (error?.code === 'PGRST204' && error?.message?.includes('bills_count')) {
        console.error('Critical schema error - bills_count column access blocked:', error);
        return { 
          data: [], 
          error: { 
            message: 'Database schema configuration error. Please contact support.',
            code: 'SCHEMA_ERROR',
            details: 'bills_count is not a database column - it should be calculated at runtime'
          }
        };
      }
      
      console.error('Error fetching vendors:', error);
      return { data: [], error };
    }
  },

  // Get vendors for dropdown/selector (simplified query)
  async getVendorsForDropdown(organizationId) {
    try {
      const { data, error } = await supabase?.from('vendors')?.select(`
          id, 
          vendor_name, 
          vendor_number,
          email,
          status
        `)?.eq('organization_id', organizationId)?.eq('status', 'active')?.order('vendor_name', { ascending: true });
      
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching vendors for dropdown:', error);
      return { data: [], error };
    }
  },

  // Create new vendor
  async createVendor(vendorData, organizationId, userId) {
    try {
      // Remove any invalid fields that don't exist in the database schema
      const cleanedData = { ...vendorData };
      
      // Remove 'type' field if it exists as it's not in the database schema
      delete cleanedData?.type;
      
      // Ensure proper field mapping based on actual database schema
      const mappedData = {
        vendor_name: cleanedData?.vendor_name,
        email: cleanedData?.email,
        phone: cleanedData?.phone || null,
        company_name: cleanedData?.company_name || null,
        website: cleanedData?.website || null,
        address_line_1: cleanedData?.address_line_1 || null,
        address_line_2: cleanedData?.address_line_2 || null,
        city: cleanedData?.city || null,
        state_province: cleanedData?.state_province || null,
        postal_code: cleanedData?.postal_code || null,
        country: cleanedData?.country || 'United States',
        tax_id: cleanedData?.tax_id || null,
        payment_terms: cleanedData?.payment_terms || 'net_30',
        credit_limit: cleanedData?.credit_limit ? parseFloat(cleanedData?.credit_limit) : 0.00,
        currency_code: cleanedData?.currency_code || 'USD',
        status: cleanedData?.status || 'active',
        notes: cleanedData?.notes || null,
        organization_id: organizationId,
        created_by: userId
      };

      // First, generate the vendor number using the database function  
      const { data: vendorNumberData, error: numberError } = await supabase
        ?.rpc('generate_vendor_number', { org_id: organizationId });

      if (numberError) {
        console.error('Error generating vendor number:', numberError);
        // If function fails, generate a fallback vendor number
        const fallbackNumber = `VEND-${Date.now()}`;
        console.warn('Using fallback vendor number:', fallbackNumber);
        
        const { data, error } = await supabase?.from('vendors')?.insert({
          ...mappedData,
          vendor_number: fallbackNumber,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.select()?.single();

        if (error) throw error;
        return { data, error: null };
      }

      const vendorNumber = vendorNumberData;

      // Create the vendor with the generated vendor number
      const { data, error } = await supabase?.from('vendors')?.insert({
          ...mappedData,
          vendor_number: vendorNumber,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) {
        // If primary insert fails due to vendor_number constraint, try with fallback
        if (error?.code === '23502' && error?.message?.includes('vendor_number')) {
          console.warn('Vendor number constraint error, retrying with fallback');
          const fallbackNumber = `VEND-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;
          
          const { data: retryData, error: retryError } = await supabase?.from('vendors')?.insert({
            ...mappedData,
            vendor_number: fallbackNumber,
            created_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          })?.select()?.single();

          if (retryError) throw retryError;
          return { data: retryData, error: null };
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error creating vendor:', error);
      return { data: null, error };
    }
  },

  // Update vendor
  async updateVendor(vendorId, updates, organizationId) {
    try {
      // CRITICAL FIX: Clean the updates object to remove any invalid fields that don't exist in database schema
      const cleanedUpdates = { ...updates };
      
      // Remove fields that are calculated at runtime and not stored in database
      delete cleanedUpdates?.bills_count; // CRITICAL: This field causes PGRST204 schema cache error
      delete cleanedUpdates?.type; // Remove type field (not in database)
      delete cleanedUpdates?.created_by_profile; // Remove profile data (not part of vendor table)
      delete cleanedUpdates?.name; // Remove auto-generated name field
      
      // Remove any other fields that might cause schema errors
      delete cleanedUpdates?.id; // Don't update the ID field
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.vendor_number; // Don't update vendor number (usually immutable)
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      
      // Ensure proper field validation for database columns only
      const validDatabaseFields = {
        vendor_name: cleanedUpdates?.vendor_name,
        email: cleanedUpdates?.email,
        phone: cleanedUpdates?.phone || null,
        company_name: cleanedUpdates?.company_name || null,
        website: cleanedUpdates?.website || null,
        address_line_1: cleanedUpdates?.address_line_1 || null,
        address_line_2: cleanedUpdates?.address_line_2 || null,
        city: cleanedUpdates?.city || null,
        state_province: cleanedUpdates?.state_province || null,
        postal_code: cleanedUpdates?.postal_code || null,
        country: cleanedUpdates?.country || null,
        tax_id: cleanedUpdates?.tax_id || null,
        payment_terms: cleanedUpdates?.payment_terms || null,
        credit_limit: cleanedUpdates?.credit_limit ? parseFloat(cleanedUpdates?.credit_limit) : null,
        currency_code: cleanedUpdates?.currency_code || null,
        status: cleanedUpdates?.status || null,
        notes: cleanedUpdates?.notes || null,
        updated_at: new Date()?.toISOString()
      };
      
      // Filter out undefined values to avoid unnecessary database operations
      const finalUpdates = {};
      Object.keys(validDatabaseFields)?.forEach(key => {
        if (validDatabaseFields?.[key] !== undefined) {
          finalUpdates[key] = validDatabaseFields?.[key];
        }
      });
      
      console.log('VENDOR UPDATE - Original data:', updates);
      console.log('VENDOR UPDATE - Cleaned data:', finalUpdates);
      
      const { data, error } = await supabase?.from('vendors')?.update(finalUpdates)?.eq('id', vendorId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) {
        // Enhanced error handling for schema errors
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error in vendor update:', error);
          throw new Error('Database schema error. The bills_count field cannot be updated as it\'s calculated at runtime.');
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      // CRITICAL: Enhanced error handling for PGRST204 schema cache errors
      if (error?.code === 'PGRST204' && error?.message?.includes('bills_count')) {
        console.error('Critical schema error - bills_count column access blocked during update:', error);
        return { 
          data: null, 
          error: { 
            message: 'Schema error: bills_count is a calculated field and cannot be updated directly.',
            code: 'SCHEMA_ERROR',
            details: 'The bills_count field is calculated at runtime and not stored in the database'
          }
        };
      }
      
      console.error('Error updating vendor:', error);
      return { data: null, error };
    }
  },

  // Enhanced delete vendor with options - NEW METHOD
  async deleteVendorWithOptions(vendorId, organizationId, options = {}) {
    try {
      const deletionOption = options?.deletionType || 'check_only';
      
      console.log(`Attempting vendor deletion with option: ${deletionOption}`, {
        vendorId,
        organizationId,
        options
      });

      // Call the enhanced database function
      const { data, error } = await supabase
        ?.rpc('delete_vendor_with_options', {
          vendor_uuid: vendorId,
          org_id: organizationId,
          deletion_option: deletionOption,
          user_uuid: options?.userId || null
        });

      if (error) {
        console.error('Error calling delete_vendor_with_options:', error);
        throw error;
      }

      console.log('Vendor deletion result:', data);

      // Log the deletion attempt for audit purposes
      if (data?.success && options?.userId) {
        try {
          await supabase?.rpc('log_vendor_deletion_attempt', {
            vendor_uuid: vendorId,
            org_id: organizationId,
            deletion_type: deletionOption,
            success: data?.success,
            error_message: data?.error || null,
            related_records_count: data?.related_records ? 
              Object.values(data?.related_records)?.reduce((sum, count) => sum + (count || 0), 0) : 0
          });
        } catch (logError) {
          console.warn('Audit logging failed:', logError);
          // Don't fail the operation if logging fails
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in deleteVendorWithOptions:', error);
      return { data: null, error };
    }
  },

  // Get vendor relationship analysis - NEW METHOD
  async getVendorRelationships(vendorId, organizationId) {
    try {
      console.log('Analyzing vendor relationships:', { vendorId, organizationId });

      // Call the relationship analysis function
      const { data, error } = await supabase
        ?.rpc('get_vendor_relationships', {
          vendor_uuid: vendorId,
          org_id: organizationId
        });

      if (error) {
        console.error('Error calling get_vendor_relationships:', error);
        throw error;
      }

      console.log('Vendor relationship analysis result:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Error in getVendorRelationships:', error);
      return { data: null, error };
    }
  },

  // Archive vendor instead of deleting - NEW METHOD
  async archiveVendor(vendorId, organizationId) {
    try {
      console.log('Archiving vendor:', { vendorId, organizationId });

      const { data, error } = await supabase
        ?.from('vendors')
        ?.update({
          status: 'archived',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', vendorId)
        ?.eq('organization_id', organizationId)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error archiving vendor:', error);
        throw error;
      }

      console.log('Vendor archived successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Error in archiveVendor:', error);
      return { data: null, error };
    }
  },

  // Delete vendor - Enhanced to handle bills_count safely and provide better constraint violation handling
  async deleteVendor(vendorId, organizationId, options = {}) {
    try {
      // First, analyze the vendor relationships
      const analysisResult = await this.getVendorRelationships(vendorId, organizationId);
      
      if (analysisResult?.error) {
        console.warn('Could not analyze vendor relationships, proceeding with basic check:', analysisResult?.error);
      }

      const relationshipData = analysisResult?.data;
      const hasConstraints = relationshipData?.has_constraints;

      // If vendor has constraints and no specific override, return constraint violation with enhanced data
      if (hasConstraints && !options?.forceDelete) {
        const totalRelated = relationshipData?.related_records ? 
          Object.values(relationshipData?.related_records)?.reduce((sum, count) => sum + (count || 0), 0) : 0;

        return { 
          data: null, 
          error: { 
            type: 'constraint_violation',
            message: `Cannot delete vendor. Has ${totalRelated} related records.`,
            constraints: ['bills', 'payments_made', 'expenses', 'vendor_credits', 'recurring_expenses'],
            relationshipData: relationshipData,
            suggestedActions: {
              archive: 'Archive vendor to preserve all records',
              cascade: totalRelated <= 10 ? 'Delete all related records (dangerous)' : 'Too many records for cascade deletion'
            }
          }
        };
      }

      // Proceed with original deletion logic for vendors without constraints
      const { count: relatedBillsCount, error: countError } = await supabase
        ?.from('bills')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('vendor_id', vendorId)
        ?.eq('organization_id', organizationId);

      if (countError) {
        console.warn('Error checking related bills, proceeding with caution:', countError);
      }

      if (relatedBillsCount > 0) {
        return { 
          data: null, 
          error: { 
            type: 'constraint_violation',
            message: `Cannot delete vendor. Has ${relatedBillsCount} related bills.`,
            constraints: ['bills'],
            relationshipData: relationshipData,
            suggestedActions: {
              archive: 'Archive vendor to preserve bill history',
              cascade: relatedBillsCount <= 10 ? 'Delete all related bills (dangerous)' : 'Too many bills for cascade deletion'
            }
          }
        };
      }

      // If no constraints, proceed with normal deletion
      const { error } = await supabase?.from('vendors')?.delete()?.eq('id', vendorId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting vendor:', error);
      return { data: null, error };
    }
  },

  // Get vendor details for viewing - Enhanced error handling and validation
  async getVendorDetails(vendorId, organizationId) {
    try {
      const { data, error } = await supabase?.from('vendors')?.select(`
          *,
          created_by_profile:user_profiles!vendors_created_by_fkey(full_name, email)
        `)?.eq('id', vendorId)?.eq('organization_id', organizationId)?.single();

      if (error) {
        // ENHANCED: Handle schema cache errors
        if (error?.code === 'PGRST204' && error?.message?.includes('bills_count')) {
          throw new Error('Schema error: bills_count is calculated at runtime, not stored in database');
        }
        throw error;
      }
      
      // Get bill count separately with enhanced error handling and validation
      let billCount = 0;
      try {
        // Ensure we have a valid vendor ID before querying
        if (vendorId && organizationId) {
          // CRITICAL: Never select bills_count column - use count query only
          const { count, error: countError } = await supabase
            ?.from('bills')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('vendor_id', vendorId)
            ?.eq('organization_id', organizationId);
        
          if (countError) {
            // Handle schema errors gracefully
            if (countError?.code === 'PGRST204' && countError?.message?.includes('bills_count')) {
              console.warn('Schema error in bill counting - using default value');
              billCount = 0;
            } else {
              console.warn(`Error fetching bill count for vendor ${vendorId}:`, countError);
              billCount = 0;
            }
          } else {
            // CRITICAL: Ensure the count is always a valid number
            billCount = typeof count === 'number' && !isNaN(count) ? count : 0;
          }
        }
      } catch (countError) {
        // ENHANCED: Handle schema cache errors in detail query
        if (countError?.code === 'PGRST204' && countError?.message?.includes('bills_count')) {
          console.warn('Schema cache error in vendor details - using fallback');
          billCount = 0;
        } else {
          console.warn(`Exception while fetching bill count for vendor ${vendorId}:`, countError);
          billCount = 0;
        }
      }
      
      return { 
        data: {
          ...data,
          bills_count: billCount // Always ensure bills_count exists as a valid number
        }, 
        error: null 
      };
    } catch (error) {
      // ENHANCED: Handle schema errors in vendor details
      if (error?.code === 'PGRST204' && error?.message?.includes('bills_count')) {
        console.error('Schema cache error in vendor details:', error);
        return { 
          data: null, 
          error: { 
            message: 'Database schema error. Please refresh and try again.',
            code: 'SCHEMA_ERROR' 
          } 
        };
      }
      
      console.error('Error fetching vendor details:', error);
      return { data: null, error };
    }
  }
};

// Enhanced Bill Service with payment integration
const billService = {
  // Get all bills for the organization
  async getBills(organizationId, filters = {}) {
    try {
      console.log('Fetching bills for organization:', organizationId, 'with filters:', filters);
      
      let query = supabase?.from('bills')?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone, company_name),
          customer:customers(id, customer_name, email, phone),
          line_items:bill_line_items(*)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        if (filters?.status === 'payable') {
          // Special filter for bills that can be paid
          query = query?.in('status', ['approved', 'partially_paid', 'overdue']);
        } else {
          query = query?.eq('status', filters?.status);
        }
      }
      
      if (filters?.vendor_id) {
        query = query?.eq('vendor_id', filters?.vendor_id);
      }
      
      if (filters?.search) {
        query = query?.or(`bill_number.ilike.%${filters?.search}%,reference_number.ilike.%${filters?.search}%`);
      }
      
      if (filters?.dateFrom) {
        query = query?.gte('issue_date', filters?.dateFrom);
      }
      
      if (filters?.dateTo) {
        query = query?.lte('issue_date', filters?.dateTo);
      }
      
      if (filters?.minAmount) {
        query = query?.gte('total_amount', parseFloat(filters?.minAmount));
      }
      
      if (filters?.maxAmount) {
        query = query?.lte('total_amount', parseFloat(filters?.maxAmount));
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching bills:', error);
        return { data: null, error: error?.message };
      }

      // Calculate balance_due for each bill
      const billsWithBalance = data?.map(bill => ({
        ...bill,
        balance_due: (bill?.total_amount || 0) - (bill?.amount_paid || 0)
      }));
      
      console.log('Successfully fetched bills:', billsWithBalance?.length);
      return { data: billsWithBalance, error: null };
    } catch (error) {
      console.error('Unexpected error fetching bills:', error);
      return { data: null, error: error?.message };
    }
  },

  // Add new method to get bills available for payment
  async getBillsForPayment(organizationId) {
    try {
      console.log('Fetching bills available for payment for organization:', organizationId);
      
      const { data, error } = await // Only bills with amount > 0
      supabase?.from('bills')?.select(`
          id,
          bill_number,
          vendor_id,
          total_amount,
          amount_paid,
          issue_date,
          due_date,
          currency_code,
          payment_terms,
          status,
          vendor:vendors(id, vendor_name)
        `)?.eq('organization_id', organizationId)?.in('status', ['approved', 'partially_paid', 'overdue'])?.gt('total_amount', 0)?.order('due_date', { ascending: true });

      if (error) {
        console.error('Error fetching bills for payment:', error);
        return { data: null, error: error?.message };
      }

      // Calculate balance_due and filter out fully paid bills
      const payableBills = data
        ?.map(bill => ({
          ...bill,
          balance_due: (bill?.total_amount || 0) - (bill?.amount_paid || 0)
        }))
        ?.filter(bill => bill?.balance_due > 0); // Only bills with outstanding balance
      
      console.log('Successfully fetched payable bills:', payableBills?.length);
      return { data: payableBills, error: null };
    } catch (error) {
      console.error('Unexpected error fetching bills for payment:', error);
      return { data: null, error: error?.message };
    }
  },

  // Create new bill - Enhanced with line items handling
  async createBill(billData, organizationId, userId) {
    try {
      console.log('Creating bill with data:', billData);
      console.log('Organization ID:', organizationId, 'User ID:', userId);
      
      // Enhanced validation before processing
      if (!organizationId || !userId) {
        throw new Error('Organization ID and User ID are required for bill creation');
      }
      
      if (!billData?.vendor_id) {
        throw new Error('Vendor selection is required');
      }
      
      if (!billData?.total_amount || parseFloat(billData?.total_amount) <= 0) {
        throw new Error('Total amount must be greater than 0');
      }
      
      // CRITICAL FIX: Extract line items data before cleaning bill data
      const lineItems = billData?.line_items || [];
      
      // Clean and map the bill data to match database schema
      const cleanedData = { ...billData };
      delete cleanedData?.type;
      delete cleanedData?.line_items; // CRITICAL: Remove line_items from bill data - handled separately
      
      const mappedData = {
        vendor_id: cleanedData?.vendor_id,
        customer_id: cleanedData?.customer_id || null,
        issue_date: cleanedData?.issue_date,
        due_date: cleanedData?.due_date,
        reference_number: cleanedData?.reference_number || null,
        subtotal: cleanedData?.subtotal ? parseFloat(cleanedData?.subtotal) : null,
        tax_amount: cleanedData?.tax_amount ? parseFloat(cleanedData?.tax_amount) : null,
        total_amount: parseFloat(cleanedData?.total_amount) || 0,
        currency_code: cleanedData?.currency_code || 'USD',
        payment_terms: cleanedData?.payment_terms || 'net_30',
        status: 'draft', // Always start as draft
        notes: cleanedData?.notes || null,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      console.log('Mapped bill data:', mappedData);

      // CRITICAL FIX: Use organization-scoped bill number generation
      let billNumber = cleanedData?.bill_number?.trim();
      
      if (!billNumber) {
        // Generate bill number with organization context
        try {
          const { data: billNumberData, error: numberError } = await supabase
            ?.rpc('generate_bill_number', { org_id: organizationId });

          if (numberError) {
            console.warn('Database function failed, using conflict resolution:', numberError);
            // Use conflict resolution function
            const { data: resolvedNumber, error: resolveError } = await supabase
              ?.rpc('resolve_bill_number_conflict', {
                attempted_number: `BILL-${new Date()?.getFullYear()}-0001`,
                org_id: organizationId,
                is_recurring_bill: false
              });
            
            if (resolveError || !resolvedNumber) {
              // Final fallback with guaranteed uniqueness
              const timestamp = Date.now();
              const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
              billNumber = `BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
            } else {
              billNumber = resolvedNumber;
            }
          } else {
            billNumber = billNumberData;
          }
        } catch (funcError) {
          console.warn('Error calling generate_bill_number function:', funcError);
          // Use timestamp-based fallback with organization context
          const timestamp = Date.now();
          const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
          billNumber = `BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
        }
      } else {
        // Manual bill number provided - validate uniqueness for this organization
        try {
          const { data: existingBill, error: checkError } = await supabase
            ?.from('bills')
            ?.select('id')
            ?.eq('bill_number', billNumber)
            ?.eq('organization_id', organizationId)
            ?.single();

          if (checkError && checkError?.code !== 'PGRST116') {
            console.warn('Error checking bill number uniqueness:', checkError);
          } else if (existingBill) {
            // Bill number already exists for this organization
            throw new Error(`Bill number ${billNumber} already exists in your organization. Please use a different number.`);
          }
        } catch (uniqueError) {
          if (uniqueError?.message?.includes('already exists')) {
            throw uniqueError;
          }
          console.warn('Could not verify bill number uniqueness, proceeding with caution:', uniqueError);
        }
      }

      console.log('Final bill number (manual or generated):', billNumber);

      // Create the bill with the bill number
      const billPayload = {
        ...mappedData,
        bill_number: billNumber
      };

      console.log('Final bill payload:', billPayload);

      // ENHANCED: Attempt creation with automatic conflict resolution
      let createdBill, error;
      let attempts = 0;
      const maxAttempts = 3;

      while (attempts < maxAttempts) {
        attempts++;
        
        const result = await supabase
          ?.from('bills')
          ?.insert(billPayload)
          ?.select(`
            *,
            vendor:vendors(id, vendor_name, email, phone),
            created_by_profile:user_profiles!bills_created_by_fkey(full_name)
          `)
          ?.single();

        createdBill = result?.data;
        error = result?.error;

        if (!error) {
          // Success!
          break;
        }

        // Handle specific constraint violations
        if (error?.code === '23505' && error?.message?.includes('idx_bills_number_org')) {
          console.warn(`Bill number collision detected (attempt ${attempts}):`, error?.message);
          
          if (attempts < maxAttempts) {
            // Try to resolve the conflict automatically
            try {
              const { data: resolvedNumber, error: resolveError } = await supabase
                ?.rpc('resolve_bill_number_conflict', {
                  attempted_number: billPayload?.bill_number,
                  org_id: organizationId,
                  is_recurring_bill: false
                });

              if (resolveError || !resolvedNumber) {
                // Final fallback
                const timestamp = Date.now();
                const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
                billPayload.bill_number = `BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
              } else {
                billPayload.bill_number = resolvedNumber;
              }
              
              console.log(`Retrying with resolved bill number: ${billPayload?.bill_number}`);
              continue;
            } catch (resolveError) {
              console.error('Error resolving bill number conflict:', resolveError);
              // Use timestamp fallback
              const timestamp = Date.now();
              const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
              billPayload.bill_number = `BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
              continue;
            }
          }
        } else if (error?.code === '23503') {
          // Foreign key constraint violation - don't retry
          if (error?.message?.includes('vendor_id')) {
            throw new Error('Selected vendor does not exist. Please refresh and try again.');
          } else if (error?.message?.includes('organization_id')) {
            throw new Error('Organization access error. Please sign out and sign back in.');
          } else if (error?.message?.includes('created_by')) {
            throw new Error('User authentication error. Please sign out and sign back in.');
          }
        } else if (error?.code === '23502') {
          // Not null constraint violation - don't retry
          throw new Error(`Required field missing: ${error?.message?.split('null value in column')?.[1]?.split(' ')?.[0] || 'unknown field'}`);
        }
        
        // For other errors, don't retry
        break;
      }

      if (error) {
        console.error('Final database insert error after all attempts:', error);
        throw new Error(`Failed to create bill: ${error?.message || 'Unknown database error'}`);
      }
      
      // CRITICAL FIX: Create line items separately after bill creation
      if (lineItems?.length > 0) {
        try {
          await this.createBillLineItems(createdBill?.id, lineItems, organizationId);
        } catch (lineItemError) {
          console.error('Error creating line items:', lineItemError);
          // Don't fail the entire operation, but log the error
          console.warn('Bill created but line items failed - user can add them manually');
        }
      }
      
      console.log('Bill created successfully:', createdBill);
      return { data: createdBill, error: null };
      
    } catch (error) {
      console.error('Error creating bill:', error);
      
      // Return structured error response
      return { 
        data: null, 
        error: {
          message: error?.message || 'Failed to create bill',
          code: error?.code,
          details: error?.details
        }
      };
    }
  },

  // CRITICAL FIX: Add method to handle bill line items separately
  async createBillLineItems(billId, lineItems, organizationId) {
    try {
      if (!lineItems || lineItems?.length === 0) {
        return { data: [], error: null };
      }

      // Map frontend line items to database structure
      const lineItemsPayload = lineItems?.map(item => ({
        bill_id: billId,
        line_number: item?.line_number || 1,
        item_id: item?.item_id || null,
        item_description: item?.item_description || '',
        quantity: parseFloat(item?.quantity) || 1,
        unit_price: parseFloat(item?.unit_price) || 0,
        tax_rate: parseFloat(item?.tax_rate) || 0,
        account_id: item?.account_id,
        notes: item?.notes || null,
        weight_per_unit: parseFloat(item?.weight_per_unit) || 1,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      }));

      const { data, error } = await supabase
        ?.from('bill_line_items')
        ?.insert(lineItemsPayload)
        ?.select();

      if (error) {
        console.error('Error creating bill line items:', error);
        throw error;
      }

      console.log('Line items created successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Error in createBillLineItems:', error);
      throw error;
    }
  },

  // Update bill - ENHANCED with line items handling and PGRST204 error fix
  async updateBill(billId, updates, organizationId) {
    try {
      // CRITICAL FIX: Extract line items before cleaning bill data
      const lineItems = updates?.line_items || [];
      const isEditingWithLineItems = lineItems?.length > 0;
      
      // CRITICAL FIX: Clean the updates object to prevent PGRST204 schema cache errors
      const cleanedUpdates = { ...updates };
      
      // Remove any fields that might cause schema cache errors
      delete cleanedUpdates?.line_items; // CRITICAL: Remove line_items field - handled separately
      delete cleanedUpdates?.created_by_profile; // Remove profile data (this causes PGRST204 error)
      delete cleanedUpdates?.approved_by_profile; // Remove profile data (not part of bills table)
      delete cleanedUpdates?.vendor?.created_by_profile; // Remove nested profile data
      delete cleanedUpdates?.customer?.created_by_profile; // Remove nested profile data (this causes the PGRST204 error)
      delete cleanedUpdates?.bills_count; // Remove calculated field
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      
      // Add proper timestamp for update
      cleanedUpdates.updated_at = new Date()?.toISOString();
      
      console.log('BILL UPDATE - Original data:', updates);
      console.log('BILL UPDATE - Cleaned data:', cleanedUpdates);
      
      const { data, error } = await supabase?.from('bills')?.update(cleanedUpdates)?.eq('id', billId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) {
        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error in bill update:', error);
          
          // Try again with even more restrictive field cleaning
          const restrictiveUpdates = {};
          const allowedFields = ['status', 'notes', 'total_amount', 'subtotal', 'tax_amount', 'due_date', 'updated_at', 'bill_number', 'vendor_id', 'payment_terms'];
          
          allowedFields?.forEach(field => {
            if (cleanedUpdates?.[field] !== undefined) {
              restrictiveUpdates[field] = cleanedUpdates?.[field];
            }
          });
          
          console.warn('Retrying bill update with restrictive field set:', restrictiveUpdates);
          
          const { data: retryData, error: retryError } = await supabase?.from('bills')?.update(restrictiveUpdates)?.eq('id', billId)?.eq('organization_id', organizationId)?.select()?.single();
          
          if (retryError) {
            throw new Error(`Schema cache error: ${retryError?.message}`);
          }
          
          // CRITICAL FIX: Handle line items separately even in retry case
          if (isEditingWithLineItems) {
            try {
              await this.updateBillLineItems(billId, lineItems, organizationId);
            } catch (lineItemError) {
              console.error('Error updating line items in retry:', lineItemError);
              console.warn('Bill updated but line items update failed');
            }
          }
          
          return { data: retryData, error: null };
        }
        
        throw error;
      }
      
      // CRITICAL FIX: Handle line items separately after successful bill update
      if (isEditingWithLineItems) {
        try {
          await this.updateBillLineItems(billId, lineItems, organizationId);
        } catch (lineItemError) {
          console.error('Error updating line items:', lineItemError);
          // Don't fail the entire operation, but log the error
          console.warn('Bill updated but line items update failed');
        }
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error updating bill:', error);
      
      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204' && error?.message?.includes('line_items')) {
        return { 
          data: null, 
          error: { 
            message: 'Schema error: line_items is not a column in the bills table. Line items are handled in a separate table.',
            code: 'SCHEMA_ERROR',
            details: 'The line_items field should not be included in bills table updates - it should be handled separately.'
          }
        };
      }
      
      return { data: null, error };
    }
  },

  // CRITICAL FIX: Add method to handle bill line items updates separately
  async updateBillLineItems(billId, lineItems, organizationId) {
    try {
      if (!lineItems || lineItems?.length === 0) {
        // If no line items provided, delete existing ones
        await supabase
          ?.from('bill_line_items')
          ?.delete()
          ?.eq('bill_id', billId);
        return { data: [], error: null };
      }

      // First, delete existing line items for this bill
      await supabase
        ?.from('bill_line_items')
        ?.delete()
        ?.eq('bill_id', billId);

      // Map frontend line items to database structure
      const lineItemsPayload = lineItems?.map(item => ({
        bill_id: billId,
        line_number: item?.line_number || 1,
        item_id: item?.item_id || null,
        item_description: item?.item_description || '',
        quantity: parseFloat(item?.quantity) || 1,
        unit_price: parseFloat(item?.unit_price) || 0,
        tax_rate: parseFloat(item?.tax_rate) || 0,
        account_id: item?.account_id,
        notes: item?.notes || null,
        weight_per_unit: parseFloat(item?.weight_per_unit) || 1,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      }));

      // Insert new line items
      const { data, error } = await supabase
        ?.from('bill_line_items')
        ?.insert(lineItemsPayload)
        ?.select();

      if (error) {
        console.error('Error updating bill line items:', error);
        throw error;
      }

      console.log('Line items updated successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Error in updateBillLineItems:', error);
      throw error;
    }
  },

  // Approve bill
  async approveBill(billId, organizationId, userId) {
    try {
      const { data, error } = await supabase?.from('bills')?.update({
          status: 'approved',
          approved_by: userId,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', billId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error approving bill:', error);
      return { data: null, error };
    }
  },

  // Delete bill
  async deleteBill(billId, organizationId) {
    try {
      const { error } = await supabase?.from('bills')?.delete()?.eq('id', billId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting bill:', error);
      return { data: null, error };
    }
  },

  // Get bill details for viewing - Enhanced to fetch line items separately
  async getBillDetails(billId, organizationId) {
    try {
      const { data, error } = await supabase?.from('bills')?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone, company_name),
          customer:customers(id, customer_name, email),
          created_by_profile:user_profiles!bills_created_by_fkey(full_name, email),
          approved_by_profile:user_profiles!bills_approved_by_fkey(full_name, email)
        `)?.eq('id', billId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;

      // CRITICAL FIX: Fetch line items separately to avoid schema cache issues
      const { data: lineItems, error: lineItemsError } = await supabase
        ?.from('bill_line_items')
        ?.select(`
          *,
          item:items(id, item_name, item_number),
          account:chart_of_accounts(id, account_name, account_code)
        `)
        ?.eq('bill_id', billId)
        ?.order('line_number');

      if (lineItemsError) {
        console.warn('Error fetching line items:', lineItemsError);
        // Continue without line items rather than failing entirely
      }

      return { 
        data: {
          ...data,
          line_items: lineItems || []
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching bill details:', error);
      return { data: null, error };
    }
  }
};

// Enhanced Payment Made Service with bill integration
const paymentMadeService = {
  // Get payments made
  async getPaymentsMade(organizationId, filters = {}) {
    try {
      let query = supabase?.from('payments_made')?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone),
          bill:bills(id, bill_number, total_amount),
          created_by_profile:user_profiles!payments_made_created_by_fkey(full_name),
          cleared_by_profile:user_profiles!payments_made_cleared_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.paymentMethod) {
        query = query?.eq('payment_method', filters?.paymentMethod);
      }
      if (filters?.vendorId) {
        query = query?.eq('vendor_id', filters?.vendorId);
      }
      if (filters?.dateFrom) {
        query = query?.gte('payment_date', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('payment_date', filters?.dateTo);
      }
      if (filters?.search) {
        query = query?.or(`payment_reference.ilike.%${filters?.search}%,reference_number.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching payments made:', error);
      return { data: [], error };
    }
  },

  // Add new method to get payments made for specific bills
  async getPaymentsForBills(billIds, organizationId) {
    try {
      console.log('Fetching payments for bills:', billIds);
      
      const { data, error } = await supabase?.from('payments_made')?.select(`
          id,
          vendor_id,
          bill_id,
          amount_paid,
          payment_date,
          payment_method,
          status,
          reference_number,
          notes
        `)?.eq('organization_id', organizationId)?.in('bill_id', billIds);

      if (error) {
        console.error('Error fetching payments for bills:', error);
        return { data: null, error: error?.message };
      }

      console.log('Successfully fetched payments for bills:', data?.length);
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Unexpected error fetching payments for bills:', error);
      return { data: null, error: error?.message };
    }
  },

  // Create new payment made with enhanced error handling
  async createPaymentMade(paymentData, organizationId, userId) {
    try {
      console.log('Creating payment made:', paymentData);
      
      // Validate required fields
      if (!paymentData?.vendor_id) {
        throw new Error('Vendor is required for payment');
      }
      
      if (!paymentData?.amount_paid || parseFloat(paymentData?.amount_paid) <= 0) {
        throw new Error('Payment amount must be greater than 0');
      }

      const { data, error } = await supabase?.from('payments_made')?.insert([{
          vendor_id: paymentData?.vendor_id,
          bill_id: paymentData?.bill_id || null, // Include bill_id if payment is for specific bill
          amount_paid: parseFloat(paymentData?.amount_paid),
          payment_date: paymentData?.payment_date,
          payment_method: paymentData?.payment_method,
          description: paymentData?.description || null,
          notes: paymentData?.notes || null,
          reference_number: paymentData?.reference_number || null,
          account_reference: paymentData?.account_reference || null,
          bank_name: paymentData?.bank_name || null,
          status: paymentData?.status || 'cleared',
          currency_code: paymentData?.currency_code || 'USD',
          organization_id: organizationId,
          created_by: userId
        }])?.select('*');

      if (error) {
        console.error('Error creating payment made:', error);
        return { data: null, error: error?.message };
      }

      console.log('Payment made created successfully:', data?.[0]);

      // If payment is for a specific bill, update the bill's amount_paid and status
      if (paymentData?.bill_id) {
        try {
          await this.updateBillPaymentStatus(paymentData?.bill_id, parseFloat(paymentData?.amount_paid), organizationId);
        } catch (billUpdateError) {
          console.warn('Payment created but bill update failed:', billUpdateError);
          // Don't fail the payment creation due to bill update error
        }
      }

      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Unexpected error creating payment made:', error);
      return { data: null, error: error?.message };
    }
  },

  // Add new method to update bill payment status
  async updateBillPaymentStatus(billId, paymentAmount, organizationId) {
    try {
      console.log(`Updating bill ${billId} with payment amount: $${paymentAmount}`);
      
      // Get current bill data
      const { data: currentBill, error: fetchError } = await supabase?.from('bills')?.select('id, total_amount, amount_paid, status')?.eq('id', billId)?.eq('organization_id', organizationId)?.single();

      if (fetchError) {
        throw new Error(`Failed to fetch bill: ${fetchError?.message}`);
      }

      // Calculate new amount_paid and determine status
      const currentAmountPaid = parseFloat(currentBill?.amount_paid) || 0;
      const newAmountPaid = currentAmountPaid + paymentAmount;
      const totalAmount = parseFloat(currentBill?.total_amount) || 0;
      
      let newStatus = currentBill?.status;
      if (newAmountPaid >= totalAmount) {
        newStatus = 'paid';
      } else if (newAmountPaid > 0) {
        newStatus = 'partially_paid';
      }

      // Update the bill
      const { data, error } = await supabase?.from('bills')?.update({
          amount_paid: newAmountPaid,
          status: newStatus,
          updated_at: new Date()?.toISOString()
        })?.eq('id', billId)?.eq('organization_id', organizationId)?.select('*');

      if (error) {
        throw new Error(`Failed to update bill: ${error?.message}`);
      }

      console.log(`Bill ${billId} updated successfully. New amount paid: $${newAmountPaid}, Status: ${newStatus}`);
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Error updating bill payment status:', error);
      return { data: null, error: error?.message };
    }
  },

  // Update payment made
  async updatePaymentMade(paymentId, updates, organizationId) {
    try {
      const { data, error } = await supabase?.from('payments_made')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', paymentId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating payment made:', error);
      return { data: null, error };
    }
  },

  // Delete payment made
  async deletePaymentMade(paymentId, organizationId) {
    try {
      const { error } = await supabase?.from('payments_made')?.delete()?.eq('id', paymentId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting payment made:', error);
      return { data: null, error };
    }
  },

  // Enhanced bulk payment processing with better error handling
  async processBulkPayments(paymentData, organizationId, userId) {
    try {
      const { billIds, paymentDate, paymentMethod, notes } = paymentData;
      
      console.log('Processing bulk payments for bills:', billIds);
      console.log('Payment details:', { paymentDate, paymentMethod, notes });
      
      // CRITICAL FIX: Validate required fields before processing
      if (!paymentMethod || paymentMethod?.trim() === '') {
        throw new Error('Payment method is required for bulk payment processing');
      }

      // Validate payment method against allowed enum values
      const validPaymentMethods = ['cash', 'check', 'bank_transfer', 'credit_card', 'debit_card', 'online', 'upi', 'wallet'];
      if (!validPaymentMethods?.includes(paymentMethod)) {
        throw new Error(`Invalid payment method: ${paymentMethod}. Must be one of: ${validPaymentMethods.join(', ')}`);
      }

      if (!paymentDate || paymentDate?.trim() === '') {
        throw new Error('Payment date is required for bulk payment processing');
      }

      // Get bill details for the bulk payment - include more status values
      const { data: bills, error: billsError } = await supabase?.from('bills')
        ?.select('id, bill_number, vendor_id, total_amount, balance_due, status')
        ?.in('id', billIds)
        ?.eq('organization_id', organizationId)
        ?.in('status', ['draft', 'pending_approval', 'approved']); // Expand allowed statuses

      if (billsError) {
        console.error('Error fetching bills for bulk payment:', billsError);
        throw new Error(`Failed to fetch bills: ${billsError?.message}`);
      }

      if (!bills || bills?.length === 0) {
        throw new Error('No valid bills found for payment processing');
      }

      console.log('Bills found for processing:', bills);

      // Process payments one by one to avoid race conditions
      const createdPayments = [];
      const failedPayments = [];

      for (let i = 0; i < bills?.length; i++) {
        const bill = bills?.[i];
        
        try {
          // Generate unique payment reference for each payment with delay to prevent conflicts
          await new Promise(resolve => setTimeout(resolve, 100 * i)); // Stagger requests
          
          let paymentReference;
          try {
            const { data: refData, error: refError } = await supabase
              ?.rpc('generate_payment_made_reference', { org_id: organizationId });
            
            if (refError) {
              console.warn('Error generating payment reference:', refError);
              const timestamp = Date.now();
              const randomSuffix = Math.random()?.toString(36)?.substr(2, 8)?.toUpperCase();
              paymentReference = `PAY-BULK-${timestamp}-${i}-${randomSuffix}`;
            } else {
              // Add batch index to make it unique
              const randomSuffix = Math.random()?.toString(36)?.substr(2, 4)?.toUpperCase();
              paymentReference = `${refData}-B${i}-${randomSuffix}`;
            }
          } catch (refError) {
            console.warn('Error calling payment reference function:', refError);
            const timestamp = Date.now();
            const randomSuffix = Math.random()?.toString(36)?.substr(2, 8)?.toUpperCase();
            paymentReference = `PAY-BULK-${timestamp}-${i}-${randomSuffix}`;
          }

          // CRITICAL FIX: Create individual payment with properly mapped field names
          const paymentPayload = {
            payment_reference: paymentReference,
            vendor_id: bill?.vendor_id,
            bill_id: bill?.id,
            amount_paid: parseFloat(bill?.balance_due || bill?.total_amount) || 0,
            payment_date: paymentDate,
            payment_method: paymentMethod, // FIXED: Ensure payment_method is properly set
            status: 'pending',
            description: `Bulk payment for bill ${bill?.bill_number}`,
            notes: notes || `Bulk payment processed for bill ${bill?.bill_number}`,
            organization_id: organizationId,
            created_by: userId,
            created_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          };

          // ENHANCED: Log payment payload for debugging
          console.log(`Creating payment for bill ${bill?.bill_number}:`, paymentPayload);

          const { data: payment, error: paymentError } = await supabase?.from('payments_made')
            ?.insert(paymentPayload)
            ?.select(`
              *,
              vendor:vendors(id, vendor_name, email),
              bill:bills(id, bill_number, total_amount)
            `)
            ?.single();

          if (paymentError) {
            console.error(`Error creating payment for bill ${bill?.bill_number}:`, paymentError);
            
            // ENHANCED: Provide more specific error messages for debugging
            let errorMessage = paymentError?.message;
            if (paymentError?.code === '23502' && paymentError?.message?.includes('payment_method')) {
              errorMessage = `Payment method is required but was null or empty. Received: ${paymentMethod}`;
            }
            
            failedPayments?.push({
              billId: bill?.id,
              billNumber: bill?.bill_number,
              error: errorMessage
            });
          } else {
            console.log(`Payment created successfully for bill ${bill?.bill_number}:`, payment);
            createdPayments?.push(payment);
          }
          
        } catch (error) {
          console.error(`Error processing payment for bill ${bill?.bill_number}:`, error);
          failedPayments?.push({
            billId: bill?.id,
            billNumber: bill?.bill_number,
            error: error?.message
          });
        }
      }

      console.log(`Bulk payment processing completed. Success: ${createdPayments?.length}, Failed: ${failedPayments?.length}`);

      // Update bill statuses for successful payments
      const billUpdatePromises = createdPayments?.map(payment => 
        supabase?.from('bills')
          ?.update({ 
            status: 'paid',
            amount_paid: parseFloat(payment?.amount_paid) || 0,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', payment?.bill_id)
          ?.eq('organization_id', organizationId)
      );

      // Execute all bill updates
      const billUpdateResults = await Promise.allSettled(billUpdatePromises);
      
      // Check for any failed updates
      const failedUpdates = billUpdateResults?.filter(result => result?.status === 'rejected');
      if (failedUpdates?.length > 0) {
        console.warn('Some bill updates failed:', failedUpdates);
      }

      // Return results
      if (failedPayments?.length > 0) {
        console.warn('Some payments failed:', failedPayments);
        return { 
          data: createdPayments, 
          error: {
            message: `${createdPayments?.length} payments created successfully, ${failedPayments?.length} failed`,
            partialSuccess: true,
            successCount: createdPayments?.length,
            failureCount: failedPayments?.length,
            failures: failedPayments
          }
        };
      }

      return { data: createdPayments, error: null };
      
    } catch (error) {
      console.error('Error processing bulk payments:', error);
      return { 
        data: null, 
        error: {
          message: error?.message || 'Failed to process bulk payments',
          code: error?.code,
          details: error?.details
        }
      };
    }
  }
};

// Expense Operations
export const expenseService = {
  async getExpenses(organizationId, filters = {}) {
    if (!organizationId) {
      return { data: [], error: { message: 'Organization ID is required' } };
    }

     try {
      let query = supabase
        ?.from('expenses')
        ?.select(`
            *,
            vendor:vendors (
              id,
              vendor_name,
              email
            ),
            account:chart_of_accounts (
              id,
              account_name,
              account_code
            ),
            created_by_user:user_profiles!created_by (
              id,
              full_name,
              email
            ),
            approved_by_user:user_profiles!approved_by (
              id,
              full_name,
              email
            )
        `)
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      if (filters?.vendorId) {
        query = query?.eq('vendor_id', filters?.vendorId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('expense_date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('expense_date', filters?.dateTo);
      }

      if (filters?.minAmount) {
        query = query?.gte('amount', parseFloat(filters?.minAmount));
      }

      if (filters?.maxAmount) {
        query = query?.lte('amount', parseFloat(filters?.maxAmount));
      }

      if (filters?.search) {
        query = query?.or(`description.ilike.%${filters?.search}%,expense_number.ilike.%${filters?.search}%,reference_number.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        return { data: [], error };
      }

      return { data: data || [], error: null };
    }  catch (error) {
      return { data: [], error: { message: error?.message || 'Failed to fetch expenses' } };
    }
  },

  async createExpense(expenseData, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      // Generate expense number
      const { data: numberData, error: numberError } = await supabase?.rpc('generate_expense_number');
      
      if (numberError) {
        return { data: null, error: numberError };
      }

      const expensePayload = {
        expense_number: numberData,
        organization_id: organizationId,
        created_by: userId,
        description: expenseData?.description,
        amount: parseFloat(expenseData?.amount) || 0,
        tax_amount: parseFloat(expenseData?.taxAmount || expenseData?.tax_amount) || 0,
        tax_rate: parseFloat(expenseData?.tax_rate) || 0,
        category: expenseData?.category || 'other',
        payment_method: expenseData?.paymentMethod || expenseData?.payment_method || 'credit_card',
        expense_date: expenseData?.expenseDate || expenseData?.expense_date || new Date()?.toISOString()?.split('T')?.[0],
        vendor_id: expenseData?.vendor || expenseData?.vendor_id || null,
        account_id: expenseData?.account_id || null,
        reference_number: expenseData?.reference || expenseData?.reference_number || null,
        is_billable: expenseData?.billable || expenseData?.is_billable || false,
        is_reimbursable: expenseData?.is_reimbursable !== undefined ? expenseData?.is_reimbursable : true,
        receipt_url: expenseData?.receipt_url || null,
        status: expenseData?.status || 'draft',
        notes: expenseData?.notes || null,
        currency_code: expenseData?.currency_code || 'USD',
        exchange_rate: parseFloat(expenseData?.exchange_rate) || 1.0000
      };

      const { data, error } = await supabase?.from('expenses')?.insert([expensePayload])?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          account:chart_of_accounts(id, account_name, account_code),
          created_by_user:user_profiles!created_by(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to create expense' } };
    }
  },

  async updateExpense(expenseId, expenseData, organizationId) {
    if (!organizationId) {
      return { data: null, error: { message: 'Organization ID is required' } };
    }

    try {
      const expensePayload = {
        description: expenseData?.description,
        amount: parseFloat(expenseData?.amount) || 0,
        tax_amount: parseFloat(expenseData?.taxAmount || expenseData?.tax_amount) || 0,
        tax_rate: parseFloat(expenseData?.tax_rate) || 0,
        category: expenseData?.category,
        payment_method: expenseData?.paymentMethod || expenseData?.payment_method,
        expense_date: expenseData?.expenseDate || expenseData?.expense_date,
        vendor_id: expenseData?.vendor || expenseData?.vendor_id || null,
        account_id: expenseData?.account_id || null,
        reference_number: expenseData?.reference || expenseData?.reference_number || null,
        is_billable: expenseData?.billable || expenseData?.is_billable || false,
        is_reimbursable: expenseData?.is_reimbursable !== undefined ? expenseData?.is_reimbursable : true,
        receipt_url: expenseData?.receipt_url || null,
        status: expenseData?.status,
        notes: expenseData?.notes || null,
        currency_code: expenseData?.currency_code || 'USD',
        exchange_rate: parseFloat(expenseData?.exchange_rate) || 1.0000,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('expenses')?.update(expensePayload)?.eq('id', expenseId)?.eq('organization_id', organizationId)?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          account:chart_of_accounts(id, account_name, account_code),
          created_by_user:user_profiles!created_by(id, full_name, email),
          approved_by_user:user_profiles!approved_by(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to update expense' } };
    }
  },

  async deleteExpense(expenseId, organizationId) {
    if (!organizationId) {
      return { data: null, error: { message: 'Organization ID is required' } };
    }

    try {
      const { data, error } = await supabase?.from('expenses')?.delete()?.eq('id', expenseId)?.eq('organization_id', organizationId);

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to delete expense' } };
    }
  },

  async approveExpense(expenseId, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      const { data, error } = await supabase?.from('expenses')?.update({
          status: 'approved',
          approved_by: userId,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', expenseId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to approve expense' } };
    }
  }
};

// Recurring Expenses Operations
export const recurringExpenseService = {
  async getRecurringExpenses(organizationId, filters = {}) {
    if (!organizationId) {
      return { data: [], error: { message: 'Organization ID is required' } };
    }

    try {
      let query = supabase?.from('recurring_expenses')?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          account:chart_of_accounts(id, account_name, account_code),
          created_by_user:user_profiles!created_by(id, full_name, email)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }
      
      if (filters?.vendorId) {
        query = query?.eq('vendor_id', filters?.vendorId);
      }
      
      if (filters?.search) {
        query = query?.or(`description.ilike.%${filters?.search}%,template_name.ilike.%${filters?.search}%,template_number.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      
      if (error) {
        return { data: [], error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message || 'Failed to fetch recurring expenses' } };
    }
  },

  async createRecurringExpense(recurringData, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      // Generate template number
      const { data: numberData, error: numberError } = await supabase?.rpc('generate_recurring_expense_number');
      
      if (numberError) {
        return { data: null, error: numberError };
      }

      const recurringPayload = {
        template_number: numberData,
        template_name: recurringData?.description || recurringData?.template_name,
        organization_id: organizationId,
        created_by: userId,
        description: recurringData?.description,
        amount: parseFloat(recurringData?.amount) || 0,
        tax_amount: parseFloat(recurringData?.tax_amount) || 0,
        tax_rate: parseFloat(recurringData?.tax_rate) || 0,
        category: recurringData?.category || 'other',
        payment_method: recurringData?.paymentMethod || recurringData?.payment_method || 'bank_transfer',
        recurring_frequency: recurringData?.frequency || recurringData?.recurring_frequency || 'monthly',
        start_date: recurringData?.startDate || recurringData?.start_date || new Date()?.toISOString()?.split('T')?.[0],
        end_date: recurringData?.endDate || recurringData?.end_date || null,
        next_expense_date: recurringData?.next_expense_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
        vendor_id: recurringData?.vendor || recurringData?.vendor_id || null,
        account_id: recurringData?.account_id || null,
        reference_number: recurringData?.reference_number || null,
        auto_approve: recurringData?.autoApprove || recurringData?.auto_approve || false,
        send_notification: recurringData?.sendNotification !== undefined ? recurringData?.sendNotification : true,
        is_billable: recurringData?.is_billable || false,
        status: recurringData?.status || 'draft',
        notes: recurringData?.notes || null,
        currency_code: recurringData?.currency_code || 'USD',
        exchange_rate: parseFloat(recurringData?.exchange_rate) || 1.0000
      };

      const { data, error } = await supabase?.from('recurring_expenses')?.insert([recurringPayload])?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          account:chart_of_accounts(id, account_name, account_code),
          created_by_user:user_profiles!created_by(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to create recurring expense' } };
    }
  },

  async updateRecurringExpense(recurringId, recurringData, organizationId) {
    if (!organizationId) {
      return { data: null, error: { message: 'Organization ID is required' } };
    }

    try {
      const recurringPayload = {
        template_name: recurringData?.description || recurringData?.template_name,
        description: recurringData?.description,
        amount: parseFloat(recurringData?.amount) || 0,
        tax_amount: parseFloat(recurringData?.tax_amount) || 0,
        tax_rate: parseFloat(recurringData?.tax_rate) || 0,
        category: recurringData?.category,
        payment_method: recurringData?.paymentMethod || recurringData?.payment_method,
        recurring_frequency: recurringData?.frequency || recurringData?.recurring_frequency,
        start_date: recurringData?.startDate || recurringData?.start_date,
        end_date: recurringData?.endDate || recurringData?.end_date || null,
        vendor_id: recurringData?.vendor || recurringData?.vendor_id || null,
        account_id: recurringData?.account_id || null,
        reference_number: recurringData?.reference_number || null,
        auto_approve: recurringData?.autoApprove || recurringData?.auto_approve,
        send_notification: recurringData?.sendNotification !== undefined ? recurringData?.sendNotification : true,
        is_billable: recurringData?.is_billable,
        status: recurringData?.status,
        is_active: recurringData?.is_active !== undefined ? recurringData?.is_active : true,
        notes: recurringData?.notes || null,
        currency_code: recurringData?.currency_code || 'USD',
        exchange_rate: parseFloat(recurringData?.exchange_rate) || 1.0000,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('recurring_expenses')?.update(recurringPayload)?.eq('id', recurringId)?.eq('organization_id', organizationId)?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          account:chart_of_accounts(id, account_name, account_code),
          created_by_user:user_profiles!created_by(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to update recurring expense' } };
    }
  },

  async deleteRecurringExpense(recurringId, organizationId) {
    if (!organizationId) {
      return { data: null, error: { message: 'Organization ID is required' } };
    }

    try {
      const { data, error } = await supabase?.from('recurring_expenses')?.delete()?.eq('id', recurringId)?.eq('organization_id', organizationId);

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to delete recurring expense' } };
    }
  }
};

// Recurring Bills Operations - FIXED: Use bills table for recurring functionality
export const recurringBillService = {
  // Get all recurring bills for the organization using bills table
  async getRecurringBills(organizationId, filters = {}) {
    try {
      let query = // Filter for recurring bills only
      supabase?.from('bills')?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone),
          created_by_profile:user_profiles!bills_created_by_fkey(full_name),
          approved_by_profile:user_profiles!bills_approved_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.eq('is_recurring', true)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.vendorId) {
        query = query?.eq('vendor_id', filters?.vendorId);
      }
      if (filters?.frequency) {
        query = query?.eq('recurring_frequency', filters?.frequency);
      }
      if (filters?.search) {
        query = query?.or(`bill_number.ilike.%${filters?.search}%,reference_number.ilike.%${filters?.search}%,notes.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching recurring bills:', error);
        throw error;
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching recurring bills:', error);
      return { data: [], error };
    }
  },

  // Create new recurring bill using bills table
  async createRecurringBill(billData, organizationId, userId) {
    try {
      // Enhanced validation before processing
      if (!organizationId || !userId) {
        throw new Error('Organization ID and User ID are required for recurring bill creation');
      }
      
      if (!billData?.vendor_id) {
        throw new Error('Vendor selection is required for recurring bills');
      }
      
      if (!billData?.total_amount || parseFloat(billData?.total_amount) <= 0) {
        throw new Error('Total amount must be greater than 0');
      }

      if (!billData?.recurring_frequency) {
        throw new Error('Recurring frequency is required');
      }

      // Verify vendor exists before proceeding
      const { data: vendorCheck, error: vendorError } = await supabase
        ?.from('vendors')
        ?.select('id, vendor_name')
        ?.eq('id', billData?.vendor_id)
        ?.eq('organization_id', organizationId)
        ?.single();
      
      if (vendorError || !vendorCheck) {
        throw new Error('Selected vendor does not exist or is not accessible. Please refresh and try again.');
      }

      // CRITICAL FIX: Use organization-scoped recurring bill number generation
      let billNumber;
      try {
        const { data: billNumberData, error: numberError } = await supabase
          ?.rpc('generate_recurring_bill_number', { org_id: organizationId });

        if (numberError) {
          console.warn('Database function failed, using conflict resolution:', numberError);
          // Use conflict resolution function for recurring bills
          const { data: resolvedNumber, error: resolveError } = await supabase
            ?.rpc('resolve_bill_number_conflict', {
              attempted_number: `REC-BILL-${new Date()?.getFullYear()}-0001`,
              org_id: organizationId,
              is_recurring_bill: true
            });
          
          if (resolveError || !resolvedNumber) {
            // Final fallback with guaranteed uniqueness
            const timestamp = Date.now();
            const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
            billNumber = `REC-BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
          } else {
            billNumber = resolvedNumber;
          }
        } else {
          billNumber = billNumberData;
        }
      } catch (funcError) {
        console.warn('Error calling generate_recurring_bill_number function:', funcError);
        // Use timestamp-based fallback with organization context
        const timestamp = Date.now();
        const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
        billNumber = `REC-BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
      }

      // Map bill data to bills table structure with recurring configuration
      const recurringBillData = {
        bill_number: billNumber,
        vendor_id: billData?.vendor_id,
        is_recurring: true, // Mark as recurring
        recurring_frequency: billData?.recurring_frequency,
        issue_date: billData?.issue_date || new Date()?.toISOString()?.split('T')?.[0],
        due_date: billData?.due_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
        next_billing_date: billData?.next_billing_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
        recurring_end_date: billData?.recurring_end_date || null,
        total_amount: parseFloat(billData?.total_amount) || 0,
        subtotal: parseFloat(billData?.subtotal) || parseFloat(billData?.total_amount) || 0,
        tax_amount: parseFloat(billData?.tax_amount) || 0.00,
        payment_terms: billData?.payment_terms || 'net_30',
        currency_code: billData?.currency_code || 'USD',
        exchange_rate: parseFloat(billData?.exchange_rate) || 1.0000,
        status: 'draft', // Start as draft
        notes: billData?.notes || `Recurring bill template: ${billData?.template_name || 'Unnamed'}`,
        reference_number: billData?.reference_number || null,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      console.log('Creating recurring bill with data:', recurringBillData);

      // ENHANCED: Attempt creation with automatic conflict resolution
      let createdBill, error;
      let attempts = 0;
      const maxAttempts = 3;

      while (attempts < maxAttempts) {
        attempts++;
        
        const result = await supabase?.from('bills')?.insert(recurringBillData)?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone),
          created_by_profile:user_profiles!bills_created_by_fkey(full_name)
        `)?.single();

        createdBill = result?.data;
        error = result?.error;

        if (!error) {
          // Success!
          break;
        }

        // Handle specific constraint violations
        if (error?.code === '23505' && error?.message?.includes('idx_bills_number_org')) {
          console.warn(`Recurring bill number collision detected (attempt ${attempts}):`, error?.message);
          
          if (attempts < maxAttempts) {
            // Try to resolve the conflict automatically
            try {
              const { data: resolvedNumber, error: resolveError } = await supabase
                ?.rpc('resolve_bill_number_conflict', {
                  attempted_number: recurringBillData?.bill_number,
                  org_id: organizationId,
                  is_recurring_bill: true
                });

              if (resolveError || !resolvedNumber) {
                // Final fallback
                const timestamp = Date.now();
                const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
                recurringBillData.bill_number = `REC-BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
              } else {
                recurringBillData.bill_number = resolvedNumber;
              }
              
              console.log(`Retrying recurring bill with resolved number: ${recurringBillData?.bill_number}`);
              continue;
            } catch (resolveError) {
              console.error('Error resolving recurring bill number conflict:', resolveError);
              // Use timestamp fallback
              const timestamp = Date.now();
              const randomSuffix = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
              recurringBillData.bill_number = `REC-BILL-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
              continue;
            }
          }
        } else if (error?.code === '23503') {
          // Foreign key constraint violation - don't retry
          if (error?.message?.includes('vendor_id')) {
            throw new Error('Selected vendor does not exist. Please refresh and try again.');
          } else if (error?.message?.includes('organization_id')) {
            throw new Error('Organization access error. Please sign out and sign back in.');
          } else if (error?.message?.includes('created_by')) {
            throw new Error('User authentication error. Please sign out and sign back in.');
          }
        } else if (error?.code === '23502') {
          // Not null constraint violation - don't retry
          const columnName = error?.message?.split('null value in column "')?.[1]?.split('"')?.[0] || 'unknown field';
          throw new Error(`Required field missing: ${columnName}. Please ensure all required fields are filled.`);
        }
        
        // For other errors, don't retry
        break;
      }

      if (error) {
        console.error('Final database insert error after all attempts:', error);
        throw new Error(`Failed to create recurring bill: ${error?.message || 'Unknown database error'}`);
      }
      
      console.log('Recurring bill created successfully:', createdBill);
      return { data: createdBill, error: null };
      
    } catch (error) {
      console.error('Error creating recurring bill:', error);
      
      return { 
        data: null, 
        error: {
          message: error?.message || 'Failed to create recurring bill',
          code: error?.code,
          details: error?.details
        }
      };
    }
  },

  // Update recurring bill using bills table
  async updateRecurringBill(billId, updates, organizationId) {
    try {
      // Clean the updates object to remove any invalid fields
      const cleanedUpdates = { ...updates };
      
      // Remove fields that don't exist in bills table
      delete cleanedUpdates?.id;
      delete cleanedUpdates?.created_at;
      delete cleanedUpdates?.organization_id;
      delete cleanedUpdates?.created_by;
      delete cleanedUpdates?.created_by_profile;
      delete cleanedUpdates?.approved_by_profile;
      delete cleanedUpdates?.balance_due; // Auto-calculated
      
      // Ensure proper field mapping for bills table
      const validUpdates = {
        vendor_id: cleanedUpdates?.vendor_id,
        bill_number: cleanedUpdates?.bill_number,
        is_recurring: true, // Always true for recurring bills
        recurring_frequency: cleanedUpdates?.recurring_frequency,
        issue_date: cleanedUpdates?.issue_date,
        due_date: cleanedUpdates?.due_date,
        next_billing_date: cleanedUpdates?.next_billing_date,
        recurring_end_date: cleanedUpdates?.recurring_end_date,
        reference_number: cleanedUpdates?.reference_number,
        subtotal: cleanedUpdates?.subtotal ? parseFloat(cleanedUpdates?.subtotal) : undefined,
        tax_amount: cleanedUpdates?.tax_amount ? parseFloat(cleanedUpdates?.tax_amount) : undefined,
        total_amount: cleanedUpdates?.total_amount ? parseFloat(cleanedUpdates?.total_amount) : undefined,
        currency_code: cleanedUpdates?.currency_code,
        exchange_rate: cleanedUpdates?.exchange_rate ? parseFloat(cleanedUpdates?.exchange_rate) : undefined,
        payment_terms: cleanedUpdates?.payment_terms,
        status: cleanedUpdates?.status,
        notes: cleanedUpdates?.notes,
        internal_notes: cleanedUpdates?.internal_notes,
        updated_at: new Date()?.toISOString()
      };
      
      // Filter out undefined values
      const finalUpdates = {};
      Object.keys(validUpdates)?.forEach(key => {
        if (validUpdates?.[key] !== undefined) {
          finalUpdates[key] = validUpdates?.[key];
        }
      });
      
      console.log('RECURRING BILL UPDATE - Final updates:', finalUpdates);
      
      const { data, error } = await supabase?.from('bills')
        ?.update(finalUpdates)
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.eq('is_recurring', true) // Ensure we're updating a recurring bill
        ?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone),
          created_by_profile:user_profiles!bills_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error updating recurring bill:', error);
      return { data: null, error };
    }
  },

  // Delete recurring bill using bills table
  async deleteRecurringBill(billId, organizationId) {
    try {
      // Check if this is actually a recurring bill before deletion
      const { data: billCheck, error: checkError } = await supabase?.from('bills')
        ?.select('is_recurring, bill_number, status')
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (checkError || !billCheck?.is_recurring) {
        return { 
          data: null, 
          error: { 
            message: 'Bill not found or is not a recurring bill template.',
            type: 'validation_error'
          }
        };
      }

      // Check for related payment records that might prevent deletion
      const { data: relatedPayments } = await supabase?.from('payments_made')
        ?.select('id')
        ?.eq('bill_id', billId)
        ?.limit(1);

      if (relatedPayments?.length > 0) {
        return { 
          data: null, 
          error: { 
            type: 'constraint_violation',
            message: 'Cannot delete recurring bill template. Has related payment records.',
            constraints: ['payments_made'] 
          }
        };
      }

      const { error } = await supabase?.from('bills')
        ?.delete()
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.eq('is_recurring', true);

      if (error) throw error;
      
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting recurring bill:', error);
      return { data: null, error };
    }
  },

  // Activate recurring bill template
  async activateRecurringBill(billId, organizationId, userId) {
    try {
      const { data, error } = await supabase?.from('bills')?.update({
          status: 'approved', // Activate by approving
          approved_by: userId,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.eq('is_recurring', true)
        ?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone),
          approved_by_profile:user_profiles!bills_approved_by_fkey(full_name)
        `)
        ?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error activating recurring bill:', error);
      return { data: null, error };
    }
  },

  // Pause recurring bill template
  async pauseRecurringBill(billId, organizationId, userId) {
    try {
      const { data, error } = await supabase?.from('bills')?.update({
          status: 'cancelled', // Use cancelled status to indicate paused
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.eq('is_recurring', true)
        ?.select()
        ?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error pausing recurring bill:', error);
      return { data: null, error };
    }
  },

  // Resume recurring bill template
  async resumeRecurringBill(billId, organizationId, userId) {
    try {
      const { data, error } = await supabase?.from('bills')?.update({
          status: 'approved', // Resume by approving again
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.eq('is_recurring', true)
        ?.select()
        ?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error resuming recurring bill:', error);
      return { data: null, error };
    }
  }
};

// Purchase Analytics and Metrics
export const purchaseAnalyticsService = {
  // Get spending metrics
  async getSpendingMetrics(organizationId) {
    try {
      // Get total spending this month
      const currentMonth = new Date();
      const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      
      const { data: monthlySpending } = await supabase?.from('bills')
        ?.select('total_amount')
        ?.eq('organization_id', organizationId)
        ?.gte('issue_date', firstDayOfMonth?.toISOString()?.split('T')?.[0])
        ?.eq('status', 'paid');

      const totalMonthlySpending = monthlySpending?.reduce((sum, bill) => 
        sum + (parseFloat(bill?.total_amount) || 0), 0) || 0;

      // Get pending bills amount - use valid status values
      const { data: pendingBills } = await supabase?.from('bills')
        ?.select('total_amount, balance_due')
        ?.eq('organization_id', organizationId)
        ?.in('status', ['pending_approval', 'approved', 'overdue']);

      const totalPendingAmount = pendingBills?.reduce((sum, bill) => 
        sum + (parseFloat(bill?.balance_due) || parseFloat(bill?.total_amount) || 0), 0) || 0;

      // Get overdue bills count and amount
      const { data: overdueBills } = await supabase?.from('bills')
        ?.select('total_amount, balance_due')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'overdue');

      const overdueCount = overdueBills?.length || 0;
      const overdueAmount = overdueBills?.reduce((sum, bill) => 
        sum + (parseFloat(bill?.balance_due) || parseFloat(bill?.total_amount) || 0), 0) || 0;

      // Get vendor count
      const { data: vendors } = await supabase?.from('vendors')
        ?.select('id')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active');

      const activeVendorCount = vendors?.length || 0;

      // Calculate average payment time
      const { data: paidBills } = await supabase?.from('bills')
        ?.select('issue_date, updated_at')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'paid')
        ?.limit(50);

      let avgPaymentTime = 30; // Default
      if (paidBills && paidBills?.length > 0) {
        const paymentTimes = paidBills?.map(bill => {
          const issueDate = new Date(bill?.issue_date);
          const paidDate = new Date(bill?.updated_at);
          return Math.floor((paidDate - issueDate) / (1000 * 60 * 60 * 24));
        })?.filter(time => time > 0);

        if (paymentTimes?.length > 0) {
          avgPaymentTime = Math.round(paymentTimes?.reduce((sum, time) => sum + time, 0) / paymentTimes?.length);
        }
      }

      return {
        data: {
          totalMonthlySpending,
          totalPendingAmount,
          overdueCount,
          overdueAmount,
          activeVendorCount,
          avgPaymentTime
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching spending metrics:', error);
      return { data: null, error };
    }
  },

  // Get spending trends (last 6 months)
  async getSpendingTrends(organizationId) {
    try {
      const trends = [];
      const currentDate = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() - i + 1, 0);
        
        const { data: monthlyBills } = await supabase?.from('bills')
          ?.select('total_amount')
          ?.eq('organization_id', organizationId)
          ?.eq('status', 'paid')
          ?.gte('issue_date', monthStart?.toISOString()?.split('T')?.[0])
          ?.lte('issue_date', monthEnd?.toISOString()?.split('T')?.[0]);

        const monthlyTotal = monthlyBills?.reduce((sum, bill) => 
          sum + (parseFloat(bill?.total_amount) || 0), 0) || 0;

        trends?.push({
          month: monthStart?.toLocaleString('default', { month: 'short', year: 'numeric' }),
          amount: monthlyTotal
        });
      }

      return { data: trends, error: null };
    } catch (error) {
      console.error('Error fetching spending trends:', error);
      return { data: [], error };
    }
  },

  // Get top vendors by spending
  async getTopVendors(organizationId, limit = 5) {
    try {
      const { data: vendorSpending } = await supabase?.from('bills')
        ?.select(`
          vendor_id,
          total_amount,
          vendor:vendors(vendor_name)
        `)
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'paid')
        ?.not('vendor_id', 'is', null);

      // Group by vendor and sum amounts
      const vendorTotals = {};
      vendorSpending?.forEach(bill => {
        const vendorId = bill?.vendor_id;
        const vendorName = bill?.vendor?.vendor_name || 'Unknown Vendor';
        
        if (!vendorTotals?.[vendorId]) {
          vendorTotals[vendorId] = {
            name: vendorName,
            totalSpent: 0
          };
        }
        
        vendorTotals[vendorId].totalSpent += parseFloat(bill?.total_amount) || 0;
      });

      // Convert to array and sort
      const topVendors = Object.values(vendorTotals)
        ?.sort((a, b) => b?.totalSpent - a?.totalSpent)
        ?.slice(0, limit);

      return { data: topVendors, error: null };
    } catch (error) {
      console.error('Error fetching top vendors:', error);
      return { data: [], error };
    }
  }
};

// Enhanced real-time subscriptions for Purchase Center
export const purchaseSubscriptions = {
  // Subscribe to vendor changes
  subscribeToVendors(organizationId, callback) {
    return supabase?.channel(`vendors-changes-${organizationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'vendors',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          callback({
            ...payload,
            table: 'vendors',
            eventType: payload?.eventType
          });
        }
      )?.subscribe();
  },

  // Subscribe to bill changes
  subscribeToBills(organizationId, callback) {
    return supabase?.channel(`bills-changes-${organizationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bills',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          callback({
            ...payload,
            table: 'bills',
            eventType: payload?.eventType
          });
        }
      )?.subscribe();
  },

  // Subscribe to expense changes
  subscribeToExpenses(organizationId, callback) {
    return supabase?.channel(`expenses-changes-${organizationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'expenses',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          callback({
            ...payload,
            table: 'expenses',
            eventType: payload?.eventType
          });
        }
      )?.subscribe();
  },

  // Subscribe to payment changes
  subscribeToPayments(organizationId, callback) {
    return supabase?.channel(`payments-purchase-changes-${organizationId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payments_made',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          callback({
            ...payload,
            table: 'payments_made',
            eventType: payload?.eventType
          });
        }
      )?.subscribe();
  },

  // Universal subscription manager
  subscribeToAllChanges(organizationId, callbacks) {
    const subscriptions = [];
    
    if (callbacks?.vendors) {
      subscriptions?.push(this?.subscribeToVendors(organizationId, callbacks?.vendors));
    }
    if (callbacks?.bills) {
      subscriptions?.push(this?.subscribeToBills(organizationId, callbacks?.bills));
    }
    if (callbacks?.expenses) {
      subscriptions?.push(this?.subscribeToExpenses(organizationId, callbacks?.expenses));
    }
    if (callbacks?.payments) {
      subscriptions?.push(this?.subscribeToPayments(organizationId, callbacks?.payments));
    }

    return {
      subscriptions,
      unsubscribeAll: () => {
        subscriptions?.forEach(sub => {
          try {
            sub?.unsubscribe();
          } catch (error) {
            console.warn('Error unsubscribing:', error);
          }
        });
      }
    };
  }
};

// Quote Operations - NEW SERVICE MODULE
export const quoteService = {
  // Get all quotes for the organization
  async getQuotes(organizationId, filters = {}) {
    try {
      let query = supabase?.from('quotes')?.select(`
          *,
          customer:customers(id, customer_name, email, phone),
          converted_to_invoice:invoices(invoice_number, total_amount),
          created_by_profile:user_profiles!quotes_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.customerId) {
        query = query?.eq('customer_id', filters?.customerId);
      }
      if (filters?.dateFrom) {
        query = query?.gte('issue_date', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('issue_date', filters?.dateTo);
      }
      if (filters?.minAmount) {
        query = query?.gte('total_amount', filters?.minAmount);
      }
      if (filters?.maxAmount) {
        query = query?.lte('total_amount', filters?.maxAmount);
      }
      if (filters?.search) {
        query = query?.or(`quote_number.ilike.%${filters?.search}%,internal_notes.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      
      if (error) {
        // CRITICAL FIX: Handle PGRST204 schema cache errors specifically for quotes table
        if (error?.code === 'PGRST204' && error?.message?.includes('converted_invoice')) {
          console.error('Schema cache error detected - incorrect column reference:', error?.message);
          
          // ENHANCED FIX: Use simplified query without the problematic join alias
          console.warn('Using simplified query to avoid schema cache error with converted_invoice column');
          
          let fallbackQuery = supabase?.from('quotes')?.select(`
              *,
              customer:customers(id, customer_name, email, phone),
              created_by_profile:user_profiles!quotes_created_by_fkey(full_name)
            `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

          // Apply same filters to fallback query
          if (filters?.status) {
            fallbackQuery = fallbackQuery?.eq('status', filters?.status);
          }
          if (filters?.customerId) {
            fallbackQuery = fallbackQuery?.eq('customer_id', filters?.customerId);
          }
          if (filters?.dateFrom) {
            fallbackQuery = fallbackQuery?.gte('issue_date', filters?.dateFrom);
          }
          if (filters?.dateTo) {
            fallbackQuery = fallbackQuery?.lte('issue_date', filters?.dateTo);
          }
          if (filters?.minAmount) {
            fallbackQuery = fallbackQuery?.gte('total_amount', filters?.minAmount);
          }
          if (filters?.maxAmount) {
            fallbackQuery = fallbackQuery?.lte('total_amount', filters?.maxAmount);
          }
          if (filters?.search) {
            fallbackQuery = fallbackQuery?.or(`quote_number.ilike.%${filters?.search}%,internal_notes.ilike.%${filters?.search}%`);
          }

          const { data: fallbackData, error: fallbackError } = await fallbackQuery;
          
          if (fallbackError) {
            throw fallbackError;
          }
          
          // CRITICAL FIX: Fetch invoice data separately using the correct column name
          const quotesWithInvoices = await Promise.allSettled(
            (fallbackData || [])?.map(async (quote) => {
              if (!quote?.converted_to_invoice_id) {
                return {
                  ...quote,
                  converted_to_invoice: null
                };
              }

              try {
                // FIXED: Use the correct column name 'converted_to_invoice_id' not 'converted_invoice'
                const { data: invoice, error: invoiceError } = await supabase
                  ?.from('invoices')
                  ?.select('invoice_number, total_amount')
                  ?.eq('id', quote?.converted_to_invoice_id)
                  ?.single();

                if (invoiceError) {
                  console.warn(`Error fetching invoice for quote ${quote?.id}:`, invoiceError);
                  return {
                    ...quote,
                    converted_to_invoice: null
                  };
                }

                return {
                  ...quote,
                  converted_to_invoice: invoice
                };
              } catch (invoiceFetchError) {
                console.warn(`Exception fetching invoice for quote ${quote?.id}:`, invoiceFetchError);
                return {
                  ...quote,
                  converted_to_invoice: null
                };
              }
            })
          );

          // Extract successful results
          const processedQuotes = quotesWithInvoices?.map((result, index) => {
            if (result?.status === 'fulfilled') {
              return result?.value;
            } else {
              console.warn('Failed to process quote with invoice:', result?.reason);
              return {
                ...fallbackData?.[index],
                converted_to_invoice: null
              };
            }
          })?.filter(quote => quote?.id);

          return { 
            data: processedQuotes || [], 
            error: null 
          };
        }
        
        throw error;
      }
      
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching quotes:', error);
      
      // Enhanced error handling for schema cache errors
      if (error?.code === 'PGRST204') {
        console.error('Critical schema error - converted_invoice column reference issue detected:', error);
        return { 
          data: [], 
          error: { 
            message: 'Database schema configuration error. The converted_invoice column does not exist - it should be converted_to_invoice_id.',
            code: 'SCHEMA_ERROR',
            details: 'Column reference issue: converted_invoice is not a valid column name in the quotes table'
          }
        };
      }
      
      return { data: [], error };
    }
  },

  // Create new quote - Fixed to handle quote_number generation properly
  async createQuote(quoteData, organizationId, userId) {
    try {
      console.log('Creating quote with data:', quoteData);
      console.log('Organization ID:', organizationId, 'User ID:', userId);
      
      // Enhanced validation before processing
      if (!organizationId || !userId) {
        throw new Error('Organization ID and User ID are required for quote creation');
      }
      
      if (!quoteData?.customer_id) {
        throw new Error('Customer selection is required');
      }
      
      if (!quoteData?.total_amount || parseFloat(quoteData?.total_amount) <= 0) {
        throw new Error('Total amount must be greater than 0');
      }
      
      // First, try to generate quote number using the database function with org_id parameter
      let quoteNumber;
      try {
        const { data: quoteNumberData, error: numberError } = await supabase
          ?.rpc('generate_quote_number', { org_id: organizationId });

        if (numberError) {
          console.warn('Database function failed:', numberError);
          // Generate fallback quote number with guaranteed uniqueness
          const timestamp = Date.now();
          const randomSuffix = Math.random()?.toString(36)?.substr(2, 4)?.toUpperCase();
          quoteNumber = `QUO-${new Date()?.getFullYear()}-${timestamp?.toString()?.slice(-6)}-${randomSuffix}`;
          console.log('Using fallback quote number:', quoteNumber);
        } else {
          quoteNumber = quoteNumberData;
          console.log('Generated quote number via function:', quoteNumber);
        }
      } catch (funcError) {
        console.warn('Error calling generate_quote_number function:', funcError);
        // Generate fallback quote number with guaranteed uniqueness
        const timestamp = Date.now();
        const randomSuffix = Math.random()?.toString(36)?.substr(2, 4)?.toUpperCase();
        quoteNumber = `QUO-${new Date()?.getFullYear()}-${timestamp?.toString()?.slice(-6)}-${randomSuffix}`;
        console.log('Using fallback quote number after function error:', quoteNumber);
      }

      // Validate that quote number was generated
      if (!quoteNumber || typeof quoteNumber !== 'string' || quoteNumber?.trim()?.length === 0) {
        // Final fallback with timestamp
        const timestamp = Date.now();
        const randomSuffix = Math.random()?.toString(36)?.substr(2, 4)?.toUpperCase();
        quoteNumber = `QUO-FALLBACK-${timestamp}-${randomSuffix}`;
        console.error('Quote number generation failed, using emergency fallback:', quoteNumber);
      }
      
      // Clean and map the quote data to match database schema
      const cleanedData = { ...quoteData };
      
      const mappedData = {
        quote_number: quoteNumber, // CRITICAL: Include quote_number in the payload
        customer_id: cleanedData?.customer_id,
        issue_date: cleanedData?.issue_date || new Date()?.toISOString()?.split('T')?.[0],
        valid_until: cleanedData?.valid_until,
        validity_period: cleanedData?.validity_period || 'days_30',
        subtotal: cleanedData?.subtotal ? parseFloat(cleanedData?.subtotal) : 0.00,
        discount_amount: cleanedData?.discount_amount ? parseFloat(cleanedData?.discount_amount) : 0.00,
        discount_percentage: cleanedData?.discount_percentage ? parseFloat(cleanedData?.discount_percentage) : 0.00,
        tax_amount: cleanedData?.tax_amount ? parseFloat(cleanedData?.tax_amount) : 0.00,
        total_amount: parseFloat(cleanedData?.total_amount) || 0,
        currency_code: cleanedData?.currency_code || 'USD',
        exchange_rate: parseFloat(cleanedData?.exchange_rate) || 1.0000,
        status: cleanedData?.status || 'draft',
        notes: cleanedData?.notes || null,
        internal_notes: cleanedData?.internal_notes || null,
        terms_and_conditions: cleanedData?.terms_and_conditions || null,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      console.log('Final quote payload with quote_number:', mappedData);

      const { data, error } = await supabase
        ?.from('quotes')
        ?.insert(mappedData)
        ?.select(`
          *,
          customer:customers(id, customer_name, email, phone),
          created_by_profile:user_profiles!quotes_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        console.error('Database insert error:', error);
        
        // Handle specific database constraints
        if (error?.code === '23505' && error?.message?.includes('quote_number')) {
          // Quote number constraint violation - try with a different number
          console.warn('Quote number collision, retrying with unique number');
          const timestamp = Date.now();
          const randomSuffix = Math.random()?.toString(36)?.substr(2, 6)?.toUpperCase();
          const fallbackNumber = `QUO-${new Date()?.getFullYear()}-${timestamp}-${randomSuffix}`;
          
          const { data: retryData, error: retryError } = await supabase
            ?.from('quotes')
            ?.insert({
              ...mappedData,
              quote_number: fallbackNumber
            })
            ?.select(`
              *,
              customer:customers(id, customer_name, email, phone),
              created_by_profile:user_profiles!quotes_created_by_fkey(full_name)
            `)
            ?.single();

          if (retryError) {
            console.error('Retry insert failed:', retryError);
            throw new Error(`Failed to create quote: ${retryError?.message || 'Unknown database error'}`);
          }
          
          console.log('Quote created successfully with fallback number:', retryData);
          return { data: retryData, error: null };
        } else if (error?.code === '23503') {
          // Foreign key constraint violation
          if (error?.message?.includes('customer_id')) {
            throw new Error('Selected customer does not exist. Please refresh and try again.');
          } else if (error?.message?.includes('organization_id')) {
            throw new Error('Organization access error. Please sign out and sign back in.');
          } else if (error?.message?.includes('created_by')) {
            throw new Error('User authentication error. Please sign out and sign back in.');
          }
        } else if (error?.code === '23502') {
          // Not null constraint violation  
          const columnName = error?.message?.split('null value in column "')?.[1]?.split('"')?.[0] || 'unknown field';
          
          if (columnName === 'quote_number') {
            throw new Error('Quote number generation failed. Please try again.');
          }
          
          throw new Error(`Required field missing: ${columnName}`);
        }
        
        throw new Error(`Failed to create quote: ${error?.message || 'Unknown database error'}`);
      }
      
      console.log('Quote created successfully:', data);
      return { data, error: null };
      
    } catch (error) {
      console.error('Error creating quote:', error);
      
      // Return structured error response
      return { 
        data: null, 
        error: {
          message: error?.message || 'Failed to create quote',
          code: error?.code,
          details: error?.details
        }
      };
    }
  },

  // Update quote - ENHANCED with better error handling for converted_to_invoice_id
  async updateQuote(quoteId, updates, organizationId) {
    try {
      // CRITICAL FIX: Clean the updates object to prevent PGRST204 schema cache errors
      const cleanedUpdates = { ...updates };
      
      // Remove any fields that might cause schema cache errors
      delete cleanedUpdates?.created_by_profile; // Remove profile data (causes PGRST204 error)
      delete cleanedUpdates?.converted_to_invoice; // Remove joined invoice data (not part of quotes table)
      delete cleanedUpdates?.customer?.created_by_profile; // Remove nested profile data
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      
      // Add proper timestamp for update
      cleanedUpdates.updated_at = new Date()?.toISOString();
      
      console.log('QUOTE UPDATE - Original data:', updates);
      console.log('QUOTE UPDATE - Cleaned data:', cleanedUpdates);
      
      const { data, error } = await supabase?.from('quotes')?.update(cleanedUpdates)?.eq('id', quoteId)?.eq('organization_id', organizationId)?.select(`
          *,
          customer:customers(id, customer_name, email, phone),
          created_by_profile:user_profiles!quotes_created_by_fkey(full_name)
        `)?.single();

      if (error) {
        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204' && error?.message?.includes('converted_invoice')) {
          console.error('Schema cache error in quote update - converted_invoice column issue:', error);
          throw new Error('Schema cache error: The converted_invoice column does not exist in the quotes table. Use converted_to_invoice_id instead.');
        }
        
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error updating quote:', error);
      
      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204' && error?.message?.includes('converted_invoice')) {
        return { 
          data: null, 
          error: { 
            message: 'Schema error: The converted_invoice column does not exist in the quotes table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The converted_invoice field should be converted_to_invoice_id, not a database column that can be updated directly.'
          }
        };
      }
      
      return { data: null, error };
    }
  },

  // Delete quote
  async deleteQuote(quoteId, organizationId) {
    try {
      // Check for related records that would prevent deletion
      const { data: relatedPaymentLinks } = await supabase?.from('payment_links')?.select('id')?.eq('reference_quote_id', quoteId)?.limit(1);
      const { data: relatedDeliveryChallans } = await supabase?.from('delivery_challans')?.select('id')?.eq('reference_quote_id', quoteId)?.limit(1);

      if (relatedPaymentLinks?.length > 0 || relatedDeliveryChallans?.length > 0) {
        return { 
          data: null, 
          error: { 
            type: 'constraint_violation',
            message: 'Cannot delete quote. Has related payment links or delivery challans.',
            constraints: ['payment_links', 'delivery_challans'] 
          }
        };
      }

      const { error } = await supabase?.from('quotes')?.delete()?.eq('id', quoteId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting quote:', error);
      return { data: null, error };
    }
  },

  // Send quote to customer
  async sendQuote(quoteId, organizationId, userId) {
    try {
      const { data, error } = await supabase?.from('quotes')?.update({
          status: 'sent',
          sent_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', quoteId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error sending quote:', error);
      return { data: null, error };
    }
  },

  // Accept quote
  async acceptQuote(quoteId, organizationId) {
    try {
      const { data, error } = await supabase?.from('quotes')?.update({
          status: 'accepted',
          accepted_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', quoteId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error accepting quote:', error);
      return { data: null, error };
    }
  },

  // Convert quote to invoice - FIXED: Use correct column name
  async convertQuoteToInvoice(quoteId, organizationId, userId) {
    try {
      // First, get the quote details
      const { data: quote, error: quoteError } = await supabase?.from('quotes')
        ?.select('*')
        ?.eq('id', quoteId)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (quoteError) throw quoteError;

      // Generate invoice number
      const { data: invoiceNumber, error: numberError } = await supabase
        ?.rpc('generate_invoice_number');

      if (numberError) {
        console.error('Error generating invoice number:', numberError);
        throw new Error('Failed to generate invoice number');
      }

      // Create invoice from quote data
      const invoiceData = {
        invoice_number: invoiceNumber,
        customer_id: quote?.customer_id,
        issue_date: new Date()?.toISOString()?.split('T')?.[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0], // 30 days from now
        subtotal: quote?.subtotal,
        discount_amount: quote?.discount_amount,
        discount_percentage: quote?.discount_percentage,
        tax_amount: quote?.tax_amount,
        total_amount: quote?.total_amount,
        currency_code: quote?.currency_code,
        exchange_rate: quote?.exchange_rate,
        status: 'draft',
        notes: quote?.notes,
        terms_and_conditions: quote?.terms_and_conditions,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      // Insert the new invoice
      const { data: invoice, error: invoiceError } = await supabase
        ?.from('invoices')
        ?.insert(invoiceData)
        ?.select()
        ?.single();

      if (invoiceError) throw invoiceError;

      // CRITICAL FIX: Update quote status and link to invoice using correct column name
      const { data: updatedQuote, error: updateError } = await supabase
        ?.from('quotes')
        ?.update({
          status: 'converted',
          converted_to_invoice_id: invoice?.id, // FIXED: Use correct column name
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', quoteId)
        ?.eq('organization_id', organizationId)
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      return { 
        data: { 
          quote: updatedQuote, 
          invoice: invoice 
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error converting quote to invoice:', error);
      return { data: null, error };
    }
  },

  // Get quote details for viewing - FIXED: Use correct column reference
  async getQuoteDetails(quoteId, organizationId) {
    try {
      const { data, error } = await supabase?.from('quotes')?.select(`
          *,
          customer:customers(id, customer_name, email, phone, company_name),
          quote_line_items(*),
          converted_to_invoice:invoices(invoice_number, status),
          created_by_profile:user_profiles!quotes_created_by_fkey(full_name, email)
        `)?.eq('id', quoteId)?.eq('organization_id', organizationId)?.single();

      if (error) {
        // ENHANCED: Handle PGRST204 schema cache errors specifically for quote details
        if (error?.code === 'PGRST204' && error?.message?.includes('converted_invoice')) {
          console.error('Schema cache error in quote details - incorrect column reference:', error);
          
          // Use simplified query without the problematic invoice join
          const { data: quoteData, error: quoteError } = await supabase?.from('quotes')?.select(`
              *,
              customer:customers(id, customer_name, email, phone, company_name),
              quote_line_items(*),
              created_by_profile:user_profiles!quotes_created_by_fkey(full_name, email)
            `)?.eq('id', quoteId)?.eq('organization_id', organizationId)?.single();

          if (quoteError) throw quoteError;

          // Fetch invoice data separately if quote is converted
          let convertedInvoice = null;
          if (quoteData?.converted_to_invoice_id) {
            try {
              const { data: invoiceData } = await supabase?.from('invoices')
                ?.select('invoice_number, status')
                ?.eq('id', quoteData?.converted_to_invoice_id)
                ?.single();
              convertedInvoice = invoiceData;
            } catch (invoiceError) {
              console.warn('Error fetching converted invoice:', invoiceError);
            }
          }

          return { 
            data: {
              ...quoteData,
              converted_to_invoice: convertedInvoice
            }, 
            error: null 
          };
        }
        
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching quote details:', error);
      return { data: null, error };
    }
  }
};

export const vendorCreditService = {
  // Get all vendor credits for the organization
  async getVendorCredits(organizationId, filters = {}) {
    if (!organizationId) {
      return { data: [], error: { message: 'Organization ID is required' } };
    }

    try {
      let query = supabase?.from('vendor_credits')?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone),
          created_by_user:user_profiles!vendor_credits_created_by_fkey(id, full_name, email),
          approved_by_user:user_profiles!vendor_credits_approved_by_fkey(id, full_name, email)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      
      if (filters?.vendorId) {
        query = query?.eq('vendor_id', filters?.vendorId);
      }
      
      if (filters?.dateFrom) {
        query = query?.gte('issue_date', filters?.dateFrom);
      }
      
      if (filters?.dateTo) {
        query = query?.lte('issue_date', filters?.dateTo);
      }
      
      if (filters?.minAmount) {
        query = query?.gte('total_credit_amount', parseFloat(filters?.minAmount));
      }
      
      if (filters?.maxAmount) {
        query = query?.lte('total_credit_amount', parseFloat(filters?.maxAmount));
      }
      
      if (filters?.search) {
        query = query?.or(`credit_number.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%,reason_description.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      
      if (error) {
        return { data: [], error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message || 'Failed to fetch vendor credits' } };
    }
  },

  // Create new vendor credit
  async createVendorCredit(creditData, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      // Generate vendor credit number
      const { data: creditNumberData, error: numberError } = await supabase
        ?.rpc('generate_vendor_credit_number', { org_id: organizationId });
      
      if (numberError) {
        console.error('Error generating vendor credit number:', numberError);
        // Generate fallback number
        const timestamp = Date.now();
        const fallbackNumber = `VC-${new Date()?.getFullYear()}-${timestamp?.toString()?.slice(-4)}`;
        
        const creditPayload = {
          credit_number: fallbackNumber,
          vendor_id: creditData?.vendor_id,
          organization_id: organizationId,
          created_by: userId,
          issue_date: creditData?.issue_date || new Date()?.toISOString()?.split('T')?.[0],
          credit_reason: creditData?.credit_reason || 'other',
          reason_description: creditData?.reason_description || null,
          description: creditData?.description,
          subtotal: parseFloat(creditData?.subtotal) || 0,
          tax_amount: parseFloat(creditData?.tax_amount) || 0,
          total_credit_amount: parseFloat(creditData?.total_credit_amount) || 0,
          currency_code: creditData?.currency_code || 'USD',
          exchange_rate: parseFloat(creditData?.exchange_rate) || 1.0000,
          status: creditData?.status || 'draft',
          notes: creditData?.notes || null
        };

        const { data, error } = await supabase?.from('vendor_credits')?.insert([creditPayload])?.select(`
            *,
            vendor:vendors(id, vendor_name, email),
            created_by_user:user_profiles!vendor_credits_created_by_fkey(id, full_name, email)
          `)?.single();

        if (error) return { data: null, error };
        return { data, error: null };
      }

      const creditPayload = {
        credit_number: creditNumberData,
        vendor_id: creditData?.vendor_id,
        organization_id: organizationId,
        created_by: userId,
        issue_date: creditData?.issue_date || new Date()?.toISOString()?.split('T')?.[0],
        credit_reason: creditData?.credit_reason || 'other',
        reason_description: creditData?.reason_description || null,
        description: creditData?.description,
        subtotal: parseFloat(creditData?.subtotal) || 0,
        tax_amount: parseFloat(creditData?.tax_amount) || 0,
        total_credit_amount: parseFloat(creditData?.total_credit_amount) || 0,
        currency_code: creditData?.currency_code || 'USD',
        exchange_rate: parseFloat(creditData?.exchange_rate) || 1.0000,
        status: creditData?.status || 'draft',
        notes: creditData?.notes || null
      };

      const { data, error } = await supabase?.from('vendor_credits')?.insert([creditPayload])?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          created_by_user:user_profiles!vendor_credits_created_by_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to create vendor credit' } };
    }
  },

  // Update vendor credit
  async updateVendorCredit(creditId, creditData, organizationId) {
    if (!organizationId) {
      return { data: null, error: { message: 'Organization ID is required' } };
    }

    try {
      const creditPayload = {
        vendor_id: creditData?.vendor_id,
        issue_date: creditData?.issue_date,
        credit_reason: creditData?.credit_reason,
        reason_description: creditData?.reason_description,
        description: creditData?.description,
        subtotal: parseFloat(creditData?.subtotal),
        tax_amount: parseFloat(creditData?.tax_amount),
        total_credit_amount: parseFloat(creditData?.total_credit_amount),
        currency_code: creditData?.currency_code,
        exchange_rate: parseFloat(creditData?.exchange_rate),
        status: creditData?.status,
        notes: creditData?.notes,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('vendor_credits')?.update(creditPayload)?.eq('id', creditId)?.eq('organization_id', organizationId)?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          created_by_user:user_profiles!vendor_credits_created_by_fkey(id, full_name, email),
          approved_by_user:user_profiles!vendor_credits_approved_by_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to update vendor credit' } };
    }
  },

  // Delete vendor credit
  async deleteVendorCredit(creditId, organizationId) {
    if (!organizationId) {
      return { data: null, error: { message: 'Organization ID is required' } };
    }

    try {
      // Check if credit has been applied
      const { data: creditCheck } = await supabase?.from('vendor_credits')
        ?.select('applied_amount, status')
        ?.eq('id', creditId)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (creditCheck?.applied_amount > 0) {
        return { 
          data: null, 
          error: { 
            type: 'constraint_violation',
            message: 'Cannot delete applied vendor credit. Cancel it first.',
            constraints: ['applied_amount'] 
          }
        };
      }

      const { data, error } = await supabase?.from('vendor_credits')?.delete()?.eq('id', creditId)?.eq('organization_id', organizationId);

      if (error) {
        return { data: null, error };
      }

      return { data: { deleted: true }, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to delete vendor credit' } };
    }
  },

  // Approve vendor credit
  async approveVendorCredit(creditId, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      const { data, error } = await supabase?.from('vendor_credits')?.update({
          status: 'issued',
          approved_by: userId,
          issued_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', creditId)?.eq('organization_id', organizationId)?.select(`
          *,
          vendor:vendors(id, vendor_name, email),
          approved_by_user:user_profiles!vendor_credits_approved_by_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to approve vendor credit' } };
    }
  },

  // Apply vendor credit to a bill
  async applyVendorCredit(creditId, billId, applyAmount, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      // Get credit details
      const { data: credit } = await supabase?.from('vendor_credits')
        ?.select('remaining_credit, status, vendor_id')
        ?.eq('id', creditId)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (!credit || credit?.status !== 'issued') {
        return { data: null, error: { message: 'Credit not available for application' } };
      }

      if (parseFloat(applyAmount) > credit?.remaining_credit) {
        return { data: null, error: { message: 'Apply amount exceeds remaining credit' } };
      }

      // Get bill details
      const { data: bill } = await supabase?.from('bills')
        ?.select('balance_due, vendor_id')
        ?.eq('id', billId)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (!bill) {
        return { data: null, error: { message: 'Bill not found' } };
      }

      if (bill?.vendor_id !== credit?.vendor_id) {
        return { data: null, error: { message: 'Credit and bill must be for the same vendor' } };
      }

      // Update vendor credit applied amount
      const { error: creditError } = await supabase?.from('vendor_credits')?.update({
          applied_amount: supabase?.rpc('increment_numeric', { 
            current_value: credit?.applied_amount || 0, 
            increment_value: parseFloat(applyAmount) 
          }),
          applied_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', creditId)?.eq('organization_id', organizationId);

      if (creditError) {
        return { data: null, error: creditError };
      }

      // Update bill balance
      const newBalance = Math.max(0, (bill?.balance_due || 0) - parseFloat(applyAmount));
      const billStatus = newBalance === 0 ? 'paid' : 'pending_payment';
      
      const { error: billError } = await supabase?.from('bills')?.update({
          balance_due: newBalance,
          status: billStatus,
          updated_at: new Date()?.toISOString()
        })?.eq('id', billId)?.eq('organization_id', organizationId);

      if (billError) {
        return { data: null, error: billError };
      }

      return { data: { appliedAmount: parseFloat(applyAmount) }, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to apply vendor credit' } };
    }
  },

  // Get vendor credit details
  async getVendorCreditDetails(creditId, organizationId) {
    try {
      const { data, error } = await supabase?.from('vendor_credits')?.select(`
          *,
          vendor:vendors(id, vendor_name, email, phone, company_name),
          vendor_credit_line_items(*),
          created_by_user:user_profiles!vendor_credits_created_by_fkey(full_name, email),
          approved_by_user:user_profiles!vendor_credits_approved_by_fkey(full_name, email)
        `)?.eq('id', creditId)?.eq('organization_id', organizationId)?.single();

      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to fetch vendor credit details' } };
    }
  },

  // Cancel vendor credit
  async cancelVendorCredit(creditId, organizationId, userId) {
    if (!organizationId || !userId) {
      return { data: null, error: { message: 'Organization ID and User ID are required' } };
    }

    try {
      // Check if credit has been applied
      const { data: creditCheck } = await supabase?.from('vendor_credits')
        ?.select('applied_amount, status')
        ?.eq('id', creditId)
        ?.eq('organization_id', organizationId)
        ?.single();

      if (creditCheck?.applied_amount > 0) {
        return { 
          data: null, 
          error: { 
            type: 'constraint_violation',
            message: 'Cannot cancel applied vendor credit. Reverse the applications first.',
            constraints: ['applied_amount'] 
          }
        };
      }

      const { data, error } = await supabase?.from('vendor_credits')?.update({
          status: 'cancelled',
          updated_at: new Date()?.toISOString()
        })?.eq('id', creditId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to cancel vendor credit' } };
    }
  }
};

export { billService, // Enhanced with payment integration
  paymentMadeService };